import {
    AlertCircle, ChevronLeft, ChevronRight, ClipboardList, Copy, Check, ExternalLink,
    Eye, Loader, Package, Printer, PlusCircle, Search, Send, Sparkles, Tag, Trash2, X,
    Download,
} from "lucide-react";
import { jsPDF } from "jspdf";
import JsBarcode from "jsbarcode";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { API_BASE } from "../../../consts";
import authService from "../../../services/AuthService";
import Papa from "papaparse";
import {
    createBatch, listBatches, listBatchItems, removeBatchItem, flushBatch, checkBatchProcessing,
    updateBatchItemQuantity, updateBatchItemPrice, updateBatchItemSku, deleteBatch,
    listCatalog,
} from "../services/catalogService";
import { createShipmentDraft, cancelWfsShipment, getPackProfile } from "../services/wfsShipmentService";
// Reusing the exact same components the real WFS Shipment wizard uses for
// its address step and its ITS-vs-Self-Split preview screen — same
// Walmart calls, same error handling, same report modal. Nothing here
// creates a real shipment on Walmart; see WfsBatchPreviewModal below.
import { ReturnAddressStep, PreviewStep } from "./ShipmentsPage";
import WalmartItemDetails from "../components/walmart/WalmartItemDetails";
import PrintLabelsModal from "../components/common/PrintLabelsModal";
import WalmartFeeSettingsModal from "../components/walmart/WalmartFeeSettingsModal";
import { estimateWalmartProfit, extractPackDetailsFromPayload, extractPriceFromPayload } from "../utils/walmartFees";

const api = (path, opts) => authService.authenticatedFetch(`${API_BASE}${path}`, opts);
const LAST_SEARCH_TYPE_KEY = "wm_batch_last_search_type";
const QUICK_BATCH_PREFIX = "__quick__";
const ENRICH_STAGGER_MS = 350;
const ENRICH_PAGE_SIZE = 10;

// Same date reformat LabelPrinter.jsx uses — YYYY-MM-DD -> MM-DD-YYYY for
// display on the label; anything else (already formatted, freeform) is
// passed through unchanged.
const formatExpiry = (val) => {
    if (!val) return '';
    const match = val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) return `${match[2]}-${match[3]}-${match[1]}`;
    return val;
};

const generateBarcodeDataUrl = (value) => {
    try {
        const canvas = document.createElement("canvas");
        const cleaned = String(value).replace(/\D/g, "");
        let format = "CODE128";
        if (cleaned.length === 12) format = "UPC";
        else if (cleaned.length === 13) format = "EAN13";
        JsBarcode(canvas, cleaned, {
            format, displayValue: false, margin: 2, width: 4, height: 100,
            background: "#ffffff", lineColor: "#000000", flat: true,
        });
        return canvas.toDataURL("image/png");
    } catch {
        return null;
    }
};

// Quick single-item print - one label size (2.25" x 1.25" thermal), printed
// `quantity` times. For anything more customizable, use the full Print Labels modal.
// Matches LabelPrinter.jsx's drawThermalLabel exactly (same insets, font
// sizes, and bottom-anchored expiration line) so a label printed from here
// looks identical to one printed from the standalone Label Printer.
const quickPrintLabel = (product) => {
    const gtin = product?.payload?.Orderable?.productIdentifiers?.productId;
    if (!gtin) {
        alert("No barcode available for this item.");
        return;
    }
    const qty = Math.max(1, parseInt(product.quantity, 10) || 1);
    const lw = 57.15, lh = 31.75; // mm, matches LabelPrinter's built-in thermal size
    const doc = new jsPDF({ unit: "mm", format: [lh, lw], orientation: "landscape" });
    const barcodeDataUrl = generateBarcodeDataUrl(gtin);
    const pad = 2, inner = lw - pad * 2;

    for (let i = 0; i < qty; i++) {
        if (i > 0) doc.addPage([lh, lw], "landscape");
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, lw, lh, "F");
        const barcodeH = lh * 0.45, barcodeY = pad;
        if (barcodeDataUrl) doc.addImage(barcodeDataUrl, "PNG", 3, barcodeY, lw - 6, barcodeH);
        const gtinY = barcodeY + barcodeH + 2.5;
        doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(0, 0, 0);
        doc.text(gtin, lw / 2, gtinY, { align: "center" });
        const titleY = gtinY + 3.5;
        doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(30, 30, 30);
        const titleLines = doc.splitTextToSize(product.title || "", inner);
        const maxTitleLines = (lw < 57.15 || lh < 31.75) ? 1 : 2;
        doc.text(titleLines.slice(0, maxTitleLines), lw / 2, titleY, { align: "center" });
        if (product.expiration_date) {
            doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(0, 0, 0);
            doc.text(`EXPIRY DATE ${formatExpiry(product.expiration_date)}`, lw / 2, lh - pad - 1, { align: "center" });
        }
    }
    doc.save(`label-${gtin}.pdf`);
};

// Walmart's Fetch Inbound Preview only needs productId/qty/pack info —
// no weight/dimensions required (unlike the real shipment flow later).
// Items with no GTIN/UPC on file yet can't be previewed and are skipped,
// with a count shown to the user rather than silently dropped.
const extractProductIdentifier = (payload) => {
    const ids = payload?.Orderable?.productIdentifiers || {};
    return { productId: ids.productId || null, productIdType: (ids.productIdType || "").toUpperCase() || null };
};

// Builds the base rows for the pack-review table. Pulls each item's REAL
// saved pack profile (vendor_pack_qty/inner_pack_qty) instead of hardcoding
// 1×1 — Walmart requires itemQty === vendorPackQty × innerPackQty exactly,
// so a wrong hardcoded pack silently breaks the preview call the same way
// it broke here originally. Falls back to 1×1 only when there's no saved
// profile yet, matching PackDetailsStep's own default.
const buildBatchPreviewRows = async (products) => {
    const rows = [];
    let skippedCount = 0;
    for (const p of products) {
        const { productId, productIdType } = extractProductIdentifier(p.payload);
        if (!productId) { skippedCount += 1; continue; }

        let profile = null;
        try { profile = (await getPackProfile(p.catalog_item_id)).profile; } catch { /* none saved yet */ }

        // Total units to ship ALWAYS comes from THIS batch item's real,
        // current quantity — never from a saved pack profile's
        // vendor_pack_qty, which is keyed only by catalog_item_id (not by
        // batch/shipment) and can be stale data left over from an entirely
        // different, earlier shipment of the same SKU. A saved profile is
        // only used to suggest HOW the current quantity should be split
        // into cases (units-per-case), never to override the quantity
        // itself.
        const shipQtyDefault = Math.max(1, parseInt(p.quantity, 10) || 1);
        const savedInnerPackQty = profile?.inner_pack_qty || 1;
        const evenlyDivisible = savedInnerPackQty > 1 && shipQtyDefault % savedInnerPackQty === 0;
        const packMode = evenlyDivisible ? "case" : "individual";
        rows.push({
            catalog_item_id: p.catalog_item_id,
            sku: p.sku,
            title: p.title,
            image_url: p.image_url,
            product_id: productId,
            product_id_type: productIdType === "GTIN" ? "GTIN" : "UPC",
            pack_mode: packMode,
            units_individual: String(shipQtyDefault),
            cases: String(evenlyDivisible ? shipQtyDefault / savedInnerPackQty : shipQtyDefault),
            units_per_case: String(evenlyDivisible ? savedInnerPackQty : 1),
        });
    }
    return { rows, skippedCount };
};

const getBatchPackNumbers = (r) => {
    if (r.pack_mode === "individual") {
        const units = parseInt(r.units_individual, 10) || 0;
        return { item_qty: units, vendor_pack_qty: units, inner_pack_qty: 1 };
    }
    const cases = parseInt(r.cases, 10) || 0;
    const unitsPerCase = parseInt(r.units_per_case, 10) || 0;
    return { item_qty: cases * unitsPerCase, vendor_pack_qty: cases, inner_pack_qty: unitsPerCase };
};

/* ─── Step 1: Batch Type Selection ─── */
export const BatchTypeStep = ({ onSelect }) => (
    <div className="max-w-4xl mx-auto py-12 px-6">
        <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 border border-teal-200 rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
                <PlusCircle size={14} /> New Inventory Batch
            </div>
            <h1 className="text-4xl font-bold text-gray-800 mb-3">Where are you sending inventory?</h1>
            <p className="text-gray-500 text-lg">Choose your fulfillment method to get started.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 [&>*:last-child:nth-child(odd)]:md:col-span-2">
            {[
                {
                    id: "walmart_wfs", icon: "🏪", title: "Walmart WFS", subtitle: "Walmart Fulfillment Services",
                    desc: "Ship to Walmart fulfillment centers. Full WFS shipment prep, label printing, and reports built in.",
                    color: "from-blue-500 to-blue-700"
                },
                {
                    id: "walmart_sf", icon: "🏬", title: "Walmart SF", subtitle: "Seller Fulfilled (Walmart)",
                    desc: "List products on Walmart and fulfill orders yourself from your own warehouse.",
                    color: "from-yellow-400 to-orange-500"
                },
                {
                    id: "amazon_fba", icon: "📦", title: "Amazon FBA", subtitle: "Fulfillment by Amazon",
                    desc: "Send inventory to Amazon fulfillment centers.", badge: "Coming Soon",
                    color: "from-orange-400 to-amber-500", disabled: true
                },
                {
                    id: "amazon_fbm", icon: "🏠", title: "Amazon FBM", subtitle: "Fulfilled by Merchant",
                    desc: "List products on Amazon and fulfill orders yourself.", badge: "Coming Soon",
                    color: "from-teal-500 to-green-600", disabled: true
                },
                {
                    id: "cross_list", icon: "🔄", title: "Cross-List", subtitle: "Amazon + Walmart, synced",
                    desc: "List the same product on both marketplaces at once, with inventory kept in sync automatically so you never oversell.",
                    badge: "Coming Soon", color: "from-purple-500 to-pink-600", disabled: true
                },
            ].map((opt) => (
                <button
                    key={opt.id}
                    onClick={() => !opt.disabled && onSelect(opt)}
                    disabled={opt.disabled}
                    className={`bg-white rounded-2xl shadow-lg p-6 border-2 border-transparent text-left group transition-all ${opt.disabled ? "opacity-50 cursor-not-allowed" : "hover:border-teal-500 hover:shadow-xl"}`}
                >
                    <div className="flex items-start gap-4">
                        <div className={`w-14 h-14 bg-gradient-to-br ${opt.color} rounded-xl flex items-center justify-center text-2xl shadow-lg flex-shrink-0 ${!opt.disabled && "group-hover:scale-110"} transition-transform`}>
                            {opt.icon}
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-lg font-bold text-gray-800">{opt.title}</h3>
                                {opt.badge && <span className="text-xs bg-gray-100 text-gray-500 font-bold px-2 py-0.5 rounded-full">{opt.badge}</span>}
                            </div>
                            <p className="text-xs text-gray-400 font-medium mb-2">{opt.subtitle}</p>
                            <p className="text-gray-600 text-sm leading-relaxed">{opt.desc}</p>
                        </div>
                    </div>
                    {!opt.disabled && (
                        <div className="mt-4 flex items-center gap-2 text-teal-600 text-sm font-semibold">
                            Select <ChevronRight size={14} />
                        </div>
                    )}
                </button>
            ))}
        </div>
    </div>
);

const AVATAR_PREVIEW_COUNT = 4;

const BatchImageStack = ({ images, total }) => {
    if (!images || images.length === 0) return null;
    const shown = images.slice(0, AVATAR_PREVIEW_COUNT);
    const remaining = total - shown.length;

    return (
        <div className="flex items-center -space-x-2">
            {shown.map((url, i) => (
                <div key={i} className="w-9 h-9 rounded-full border-[3px] border-white shadow-sm bg-gray-100 overflow-hidden flex-shrink-0" style={{ zIndex: shown.length - i }}>
                    {url ? (
                        <img src={url} alt="" className="w-full h-full object-cover" />
                    ) : (
                        <span className="w-full h-full flex items-center justify-center text-xs">📦</span>
                    )}
                </div>
            ))}
            {remaining > 0 && (
                <div className="w-9 h-9 rounded-full border-[3px] border-white shadow-sm bg-gray-700 text-white flex items-center justify-center text-[11px] font-bold flex-shrink-0" style={{ zIndex: 0 }}>
                    +{remaining}
                </div>
            )}
        </div>
    );
};

/* ─── Step 1a: Resume an open batch, or start new ─── */
export const ResumeBatchStep = ({ onSelect, onStartNew, onQuickStart, onConvertToWfs, convertingBatchId }) => {
    const [quickStarting, setQuickStarting] = useState(null); // "walmart_wfs" | "walmart_sf" | null
    const [batches, setBatches] = useState(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        listBatches("open")
            .then((d) => setBatches((d.batches || []).filter((b) => !b.name.startsWith(QUICK_BATCH_PREFIX))))
            .catch(() => setBatches([]));
    }, []);

    const handleDelete = async (batchId) => {
        setDeleting(true);
        try {
            await deleteBatch(batchId);
            setBatches((prev) => prev.filter((b) => b.id !== batchId));
        } catch (err) {
            console.error("[deleteBatch failed]", err);
        } finally {
            setDeleting(false);
            setConfirmDeleteId(null);
        }
    };

    return (
        <div className="max-w-2xl mx-auto py-12 px-6">
            <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">Batches</h1>

            {batches === null && <p className="text-center text-gray-400 text-sm">Loading...</p>}

            {batches !== null && batches.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-4 mb-5 space-y-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase px-2 mb-1">Continue an Open Batch</p>
                    {batches.map((b) => (
                        <div key={b.id} className="w-full flex items-center justify-between px-4 py-3 rounded-xl hover:bg-teal-50 transition">
                            <button onClick={() => onSelect(b)} className="flex-1 text-left min-w-0">
                                <p className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                                    {b.name}
                                    {b.batch_type === "walmart_sf" && onConvertToWfs && (
                                        <span
                                            onClick={(e) => { e.stopPropagation(); onConvertToWfs(b); }}
                                            className="text-[10px] font-bold text-blue-600 hover:text-blue-700 border border-blue-200 rounded-full px-2 py-0.5 whitespace-nowrap cursor-pointer"
                                        >
                                            {convertingBatchId === b.id ? "Converting..." : "Convert WFS →"}
                                        </span>
                                    )}
                                </p>
                                <p className="text-xs text-gray-400 flex items-center gap-1.5 flex-wrap">
                                    {b.batch_type.replace("_", " ").toUpperCase()} · updated {new Date(b.updated_at).toLocaleString()}
                                    {confirmDeleteId === b.id ? (
                                        <span className="inline-flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                            <span className="text-red-500 font-semibold">Delete this batch?</span>
                                            <button
                                                onClick={() => handleDelete(b.id)}
                                                disabled={deleting}
                                                className="text-red-600 font-bold hover:underline disabled:opacity-50"
                                            >
                                                {deleting ? "Deleting..." : "Yes, delete"}
                                            </button>
                                            <button onClick={() => setConfirmDeleteId(null)} className="text-gray-400 hover:text-gray-600">
                                                Cancel
                                            </button>
                                        </span>
                                    ) : (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(b.id); }}
                                            className="text-red-400 hover:text-red-600 font-semibold inline-flex items-center gap-0.5"
                                        >
                                            <Trash2 size={10} /> Delete
                                        </button>
                                    )}
                                </p>
                            </button>
                            <div className="flex items-center gap-2 flex-shrink-0" onClick={() => onSelect(b)}>
                                <BatchImageStack images={b.preview_images || []} total={b.batch_items?.[0]?.count ?? 0} />
                                <span className="text-xs bg-teal-100 text-teal-700 font-bold px-2.5 py-1 rounded-full">
                                    {b.batch_items?.[0]?.count ?? 0} items
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <button
                onClick={onStartNew}
                className="w-full py-4 border-2 border-dashed border-gray-300 hover:border-teal-400 rounded-2xl text-gray-500 hover:text-teal-600 font-semibold transition flex items-center justify-center gap-2"
            >
                <PlusCircle size={16} /> Start a New Batch
            </button>

            <div className="grid grid-cols-2 gap-3 mt-3">
                <button
                    onClick={async () => { setQuickStarting("walmart_wfs"); try { await onQuickStart("walmart_wfs"); } finally { setQuickStarting(null); } }}
                    disabled={!!quickStarting}
                    className="py-3 border-2 border-dashed border-blue-200 hover:border-blue-400 rounded-2xl text-blue-500 hover:text-blue-600 text-sm font-semibold transition disabled:opacity-50"
                >
                    {quickStarting === "walmart_wfs" ? "Starting..." : "Continue with no batch — WFS items"}
                </button>
                <button
                    onClick={async () => { setQuickStarting("walmart_sf"); try { await onQuickStart("walmart_sf"); } finally { setQuickStarting(null); } }}
                    disabled={!!quickStarting}
                    className="py-3 border-2 border-dashed border-yellow-200 hover:border-yellow-400 rounded-2xl text-yellow-600 hover:text-yellow-700 text-sm font-semibold transition disabled:opacity-50"
                >
                    {quickStarting === "walmart_sf" ? "Starting..." : "Continue with no batch — SF items"}
                </button>
            </div>
        </div>
    );
};

/* ─── Step 2: Name the Batch (creates the DB row) ─── */
export const BatchNameStep = ({ batchType, onSubmit, onBack }) => {
    const [name, setName] = useState("");
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState("");
    const suggestedName = `${batchType.title} Batch - ${new Date().toLocaleDateString()}`;

    const handleContinue = async (destination = "search") => {
        setCreating(true);
        setError("");
        try {
            const { batch } = await createBatch(name.trim() || suggestedName, batchType.id);
            onSubmit(batch, destination);
        } catch (err) {
            setError(err.message || "Failed to create batch");
        } finally {
            setCreating(false);
        }
    };

    return (
        <div className="max-w-lg mx-auto py-12 px-6">
            <div className="flex items-center gap-3 mb-8">
                <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition">
                    <ChevronLeft size={20} />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Name This Batch</h1>
                    <p className="text-gray-500 text-sm">
                        Batch Type: <span className="font-semibold text-teal-600">{batchType.title}</span>
                    </p>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6">
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Batch Name</label>
                <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !creating && handleContinue("search")}
                    placeholder={suggestedName}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-500 mb-2"
                    autoFocus
                />
                <button onClick={() => setName(suggestedName)} className="text-xs text-teal-600 hover:text-teal-700 font-semibold mb-5">
                    Use suggested name
                </button>

                {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm">{error}</div>}

                <button
                    onClick={() => handleContinue("search")}
                    disabled={creating}
                    className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 mb-2.5"
                >
                    {creating ? "Creating..." : <>Search Walmart Catalog <ChevronRight size={16} /></>}
                </button>
                <button
                    onClick={() => handleContinue("buylist_pick")}
                    disabled={creating}
                    className="w-full py-3 border-2 border-dashed border-purple-300 text-purple-600 rounded-xl font-bold hover:bg-purple-50 transition flex items-center justify-center gap-2 disabled:opacity-50"
                >
                    📋 Import Items from Buy List
                </button>
            </div>
        </div>
    );
};

// Same fixed-positioning-via-getBoundingClientRect trick as ProductImage's
// hover preview below — a plain CSS `absolute` popover gets clipped by the
// item list's `overflow-y-auto` container regardless of top/bottom
// direction, since the clipping ancestor is the scroll container itself,
// not whichever edge the popover opens toward.
const FeeBreakdownHover = ({ est, isWFSBatch }) => {
    const [pos, setPos] = useState(null); // {top, left} in viewport coords, or null when not hovering
    const anchorRef = useRef(null);
    const popoverWidth = 208; // w-52

    const handleEnter = () => {
        const rect = anchorRef.current?.getBoundingClientRect();
        if (!rect) return;
        const left = Math.min(Math.max(rect.left, 8), window.innerWidth - popoverWidth - 8);
        const top = Math.max(rect.top - 8, 8); // popover sits ABOVE the anchor, so anchor its bottom edge to rect.top
        setPos({ top, left });
    };

    return (
        <span
            ref={anchorRef}
            className="cursor-help"
            onMouseEnter={handleEnter}
            onMouseLeave={() => setPos(null)}
        >
            Fees: <span className="font-semibold text-red-500 underline decoration-dotted">${est.totalFees.toFixed(2)}</span>
            {pos && (
                <div
                    className="fixed z-50 bg-white border border-gray-200 rounded-xl shadow-2xl p-3 text-left normal-case pointer-events-none"
                    style={{ top: pos.top, left: pos.left, width: popoverWidth, transform: "translateY(-100%)" }}
                >
                    <p className="text-[10px] font-bold text-gray-500 uppercase mb-1.5">Fee Breakdown (per unit)</p>
                    <div className="space-y-1 text-xs">
                        <div className="flex justify-between"><span className="text-gray-500">Referral Fee</span><span className="font-semibold text-gray-700">${est.referralFee.toFixed(2)}</span></div>
                        {isWFSBatch ? (
                            <>
                                <div className="flex justify-between"><span className="text-gray-500">WFS Fee</span><span className="font-semibold text-gray-700">${est.wfsFee.toFixed(2)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">Inbound Shipping</span><span className="font-semibold text-gray-700">${est.inboundShipping.toFixed(2)}</span></div>
                            </>
                        ) : (
                            <div className="flex justify-between"><span className="text-gray-500">Fulfillment Cost</span><span className="font-semibold text-gray-700">${est.fulfillmentCost.toFixed(2)}</span></div>
                        )}
                        <div className="flex justify-between"><span className="text-gray-500">Sales Tax (on cost)</span><span className="font-semibold text-gray-700">${est.salesTax.toFixed(2)}</span></div>
                        <div className="flex justify-between pt-1 border-t border-gray-100"><span className="text-gray-600 font-bold">Total</span><span className="font-bold text-red-600">${est.totalFees.toFixed(2)}</span></div>
                    </div>
                </div>
            )}
        </span>
    );
};

const ProductImage = ({ src, fallback = "📦", className = "" }) => {
    const [errored, setErrored] = useState(false);
    const [hoverPos, setHoverPos] = useState(null); // {top, left} in viewport coords, or null when not hovering
    const imgRef = useRef(null);
    if (!src || errored) return <span className={`text-3xl ${className}`}>{fallback}</span>;

    const handleEnter = () => {
        const rect = imgRef.current?.getBoundingClientRect();
        if (!rect) return;
        // Position to the right of the thumbnail; flip to the left if there's
        // not enough room on the right side of the viewport.
        const previewSize = 220;
        const spaceOnRight = window.innerWidth - rect.right;
        const left = spaceOnRight > previewSize + 16 ? rect.right + 8 : rect.left - previewSize - 8;
        const top = Math.min(Math.max(rect.top + rect.height / 2 - previewSize / 2, 8), window.innerHeight - previewSize - 8);
        setHoverPos({ top, left });
    };

    return (
        <>
            <img ref={imgRef} src={src} alt="" onError={() => setErrored(true)}
                onMouseEnter={handleEnter} onMouseLeave={() => setHoverPos(null)}
                className={`object-contain rounded ${className}`} style={{ width: 48, height: 48 }} />
            {hoverPos && (
                // position: fixed escapes the items list's overflow-y-auto
                // clipping (absolute positioning was getting cut off there).
                <div className="fixed z-50 bg-white border border-gray-200 rounded-xl shadow-2xl p-2 pointer-events-none"
                    style={{ top: hoverPos.top, left: hoverPos.left }}>
                    <img src={src} alt="" className="rounded-lg object-contain" style={{ width: 220, height: 220 }} />
                </div>
            )}
        </>
    );
};

// (?) badge next to a failed item — opens a centered modal showing
// Walmart's actual error_message (set by the backend from the feed's
// itemIngestionStatus[].ingestionErrors) in full, unclipped.
const ErrorPopover = ({ message }) => {
    const [open, setOpen] = useState(false);
    if (!message) return null;
    return (
        <>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                className="ml-1 w-3.5 h-3.5 inline-flex items-center justify-center rounded-full bg-red-100 text-red-600 text-[9px] font-bold hover:bg-red-200"
                title="Why did this fail?"
            >
                ?
            </button>
            {open && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                    onClick={(e) => { e.stopPropagation(); setOpen(false); }}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
                        onClick={(e) => e.stopPropagation()}>
                        <div className="bg-red-50 border-b border-red-100 px-5 py-3 flex items-center justify-between">
                            <h3 className="font-bold text-red-700 text-sm flex items-center gap-2">
                                <AlertCircle size={16} /> Why did this fail?
                            </h3>
                            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="p-5 text-sm text-gray-700 leading-relaxed max-h-[60vh] overflow-y-auto">
                            {message}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// Same inline-edit pattern as QuantityEditor, for price — editable at any
// status (queued/processing just updates the stored payload; processed
// pushes live to Walmart, handled server-side by the /price endpoint).
const PriceEditor = ({ item, batchId, onChanged }) => {
    const [editing, setEditing] = useState(false);
    const currentPrice = item.payload?.Orderable?.price;
    const [value, setValue] = useState(currentPrice ?? "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const save = async (e) => {
        e.stopPropagation();
        const price = parseFloat(value);
        if (isNaN(price) || price < 0) { setError("Invalid price"); return; }
        setSaving(true);
        setError("");
        try {
            await updateBatchItemPrice(batchId, item.id, price);
            setEditing(false);
            onChanged();
        } catch (err) {
            setError(err.message || "Failed");
        } finally {
            setSaving(false);
        }
    };

    if (!editing) {
        return (
            <button onClick={(e) => { e.stopPropagation(); setValue(currentPrice ?? ""); setEditing(true); }}
                className="text-[10px] font-semibold text-gray-600 hover:text-teal-600 underline decoration-dotted">
                {currentPrice != null ? `$${currentPrice}` : "Set price"}
            </button>
        );
    }

    return (
        <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <span className="text-[10px] text-gray-400">$</span>
            <input
                type="number" min="0" step="0.01" value={value} autoFocus
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && save(e)}
                className="w-16 border border-gray-200 rounded px-1 py-0.5 text-[10px] focus:outline-none focus:border-teal-500"
            />
            <button onClick={save} disabled={saving} className="text-[10px] text-teal-600 font-bold disabled:opacity-50">
                {saving ? "..." : "Save"}
            </button>
            <button onClick={(e) => { e.stopPropagation(); setEditing(false); }} className="text-[10px] text-gray-400">✕</button>
            {error && <span className="text-[9px] text-red-500">{error}</span>}
        </span>
    );
};

// SKU is only editable while queued/processing — Walmart doesn't allow
// changing the SKU of an item it's already processed, so once the item is
// past that stage this becomes copy-only (no edit affordance shown).
const SkuEditor = ({ item, batchId, onChanged }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(item.sku ?? "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [copied, setCopied] = useState(false);
    const canEdit = item.status === "queued" || item.status === "processing";

    const copy = (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(item.sku || "");
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
    };

    const save = async (e) => {
        e.stopPropagation();
        if (!value.trim()) { setError("Required"); return; }
        setSaving(true);
        setError("");
        try {
            await updateBatchItemSku(batchId, item.id, value.trim());
            setEditing(false);
            onChanged();
        } catch (err) {
            setError(err.message || "Failed");
        } finally {
            setSaving(false);
        }
    };

    if (!editing) {
        return (
            <span className="inline-flex items-center gap-1">
                <button
                    onClick={(e) => { e.stopPropagation(); if (canEdit) { setValue(item.sku ?? ""); setEditing(true); } }}
                    disabled={!canEdit}
                    title={canEdit ? "Click to edit" : "SKU is locked once Walmart has processed this item"}
                    className={`text-[10px] text-gray-400 flex items-center gap-1 ${canEdit ? "hover:text-teal-600 underline decoration-dotted" : "cursor-default"}`}
                >
                    <Tag size={9} /> {item.sku}
                </button>
                <button onClick={copy} title="Copy SKU" className="text-gray-300 hover:text-teal-600">
                    {copied ? <Check size={11} /> : <Copy size={11} />}
                </button>
            </span>
        );
    }

    return (
        <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <input
                value={value} autoFocus
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && save(e)}
                className="w-24 border border-gray-200 rounded px-1 py-0.5 text-[10px] font-mono focus:outline-none focus:border-teal-500"
            />
            <button onClick={save} disabled={saving} className="text-[10px] text-teal-600 font-bold disabled:opacity-50">
                {saving ? "..." : "Save"}
            </button>
            <button onClick={(e) => { e.stopPropagation(); setEditing(false); }} className="text-[10px] text-gray-400">✕</button>
            {error && <span className="text-[9px] text-red-500">{error}</span>}
        </span>
    );
};

// Inline quantity editor — works at any status: while queued/processing it
// just updates the stored quantity (picked up whenever the item resolves);
// once processed, it calls the live inventory endpoint immediately (handled
// server-side by PATCH /api/batches/<id>/items/<id>/quantity).
const QuantityEditor = ({ item, batchId, onChanged }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(item.quantity ?? "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const save = async (e) => {
        e.stopPropagation();
        const qty = parseInt(value, 10);
        if (isNaN(qty) || qty < 0) { setError("Invalid qty"); return; }
        setSaving(true);
        setError("");
        try {
            await updateBatchItemQuantity(batchId, item.id, qty);
            setEditing(false);
            onChanged();
        } catch (err) {
            setError(err.message || "Failed");
        } finally {
            setSaving(false);
        }
    };

    if (!editing) {
        return (
            <button onClick={(e) => { e.stopPropagation(); setValue(item.quantity ?? ""); setEditing(true); }}
                className="text-[10px] text-gray-400 hover:text-teal-600 underline decoration-dotted">
                Qty: {item.quantity ?? "—"}
            </button>
        );
    }

    return (
        <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <input
                type="number" min="0" value={value} autoFocus
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && save(e)}
                className="w-14 border border-gray-200 rounded px-1 py-0.5 text-[10px] focus:outline-none focus:border-teal-500"
            />
            <button onClick={save} disabled={saving} className="text-[10px] text-teal-600 font-bold disabled:opacity-50">
                {saving ? "..." : "Save"}
            </button>
            <button onClick={(e) => { e.stopPropagation(); setEditing(false); }} className="text-[10px] text-gray-400">✕</button>
            {error && <span className="text-[9px] text-red-500">{error}</span>}
        </span>
    );
};

/* ─── WFS Shipment Preview (batch-level, non-committal) ───
   Builds a TEMPORARY local draft from the batch's current items, asks
   Walmart for the ITS-vs-Self-Split preview (read-only call — same one
   the real Shipments wizard uses), shows it, then deletes the temporary
   draft when closed. Never calls createShipmentOnWalmart, so nothing is
   ever actually created on Walmart's side. */
const WfsBatchPreviewModal = ({ batchId, products, onClose }) => {
    const [phase, setPhase] = useState("loading"); // 'loading' | 'pack' | 'address' | 'preview' | 'handoff'
    const [rows, setRows] = useState(null);
    const [skippedCount, setSkippedCount] = useState(0);
    const [shipmentId, setShipmentId] = useState(null);
    const [createError, setCreateError] = useState("");
    const cleanupDone = useRef(false);

    useEffect(() => {
        (async () => {
            const { rows: builtRows, skippedCount: skipped } = await buildBatchPreviewRows(products);
            setRows(builtRows);
            setSkippedCount(skipped);
            setPhase("pack");
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClose = () => {
        // Only auto-deletes if the user is BAILING OUT (closing the ✕, or
        // backing out of pack/address/preview without confirming a choice).
        // Once they've clicked "Confirm & Continue" (see handleConfirmed
        // below), cleanupDone is already set to true, so this becomes a
        // no-op delete-wise — the draft stays, since that now reflects
        // real intent to finish the shipment in the Shipments page.
        if (shipmentId && !cleanupDone.current) {
            cleanupDone.current = true;
            // Status here is always 'draft' or 'previewed' — never 'created' —
            // so this only ever deletes our own local row. Best-effort: if it
            // fails, worst case an unused draft sits harmlessly in All Shipments.
            cancelWfsShipment(shipmentId).catch(() => { });
        }
        onClose();
    };

    // Called when the user clicks "Confirm & Continue" inside PreviewStep.
    // Their ITS/Self-Split choice is already saved on the draft by that
    // point (selectShipmentPreview already ran) — from here on this is a
    // real, resumable draft, not a throwaway preview, so we stop the
    // auto-cleanup and just point them at where to actually finish it.
    const handleConfirmed = () => {
        cleanupDone.current = true;
        setPhase("handoff");
    };

    const handleDiscardDraft = () => {
        if (shipmentId) cancelWfsShipment(shipmentId).catch(() => { });
        onClose();
    };

    const updateRow = (idx, field, val) => setRows((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], [field]: val };
        return next;
    });

    const updatePackMode = (idx, mode) => setRows((prev) => {
        const next = [...prev];
        const r = next[idx];
        if (mode === r.pack_mode) return prev;
        if (mode === "case") {
            const total = parseInt(r.units_individual, 10) || 1;
            next[idx] = { ...r, pack_mode: "case", cases: String(total), units_per_case: "1" };
        } else {
            const cases = parseInt(r.cases, 10) || 1;
            const perCase = parseInt(r.units_per_case, 10) || 1;
            next[idx] = { ...r, pack_mode: "individual", units_individual: String(cases * perCase) };
        }
        return next;
    });

    const hasInvalidQty = rows?.some((r) => r.pack_mode === "individual"
        ? (!r.units_individual || parseInt(r.units_individual, 10) <= 0)
        : (!r.cases || parseInt(r.cases, 10) <= 0 || !r.units_per_case || parseInt(r.units_per_case, 10) <= 0));

    const handleAddressNext = async (addr) => {
        setCreateError("");
        try {
            const items = rows.map((r) => {
                const { item_qty, vendor_pack_qty, inner_pack_qty } = getBatchPackNumbers(r);
                return {
                    catalog_item_id: r.catalog_item_id, sku: r.sku, title: r.title, image_url: r.image_url,
                    product_id: r.product_id, product_id_type: r.product_id_type,
                    item_desc: r.title, item_qty, vendor_pack_qty, inner_pack_qty,
                };
            });
            const { shipment } = await createShipmentDraft({ source_batch_id: batchId, return_address: addr, items });
            setShipmentId(shipment.id);
            setPhase("preview");
        } catch (e) {
            setCreateError(e.message || "Failed to build the preview draft");
            throw e; // let ReturnAddressStep's own error UI show it too
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto">
            <div className="min-h-full flex items-start justify-center py-6 px-4">
                <div className="bg-gray-50 rounded-2xl shadow-2xl w-full max-w-4xl relative">
                    <button onClick={handleClose} className="absolute top-4 right-4 z-10 text-gray-400 hover:text-gray-600 bg-white rounded-full p-1.5 shadow">
                        <X size={18} />
                    </button>

                    {phase === "loading" && (
                        <div className="p-10 flex items-center gap-2 text-gray-400">
                            <Loader size={18} className="animate-spin" /> Loading pack details...
                        </div>
                    )}

                    {phase === "pack" && (
                        <div className="p-6">
                            <h2 className="text-xl font-bold text-gray-800 mb-1">Confirm Pack Quantities</h2>
                            <p className="text-gray-500 text-sm mb-4">
                                Choose Individual Units or Case Pack for each item and enter its quantity.
                            </p>

                            {skippedCount > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-700">
                                    {skippedCount} item(s) skipped from this preview — no GTIN/UPC on file yet.
                                </div>
                            )}
                            {rows.length === 0 ? (
                                <p className="text-sm text-gray-400">No previewable items in this batch.</p>
                            ) : (
                                <>
                                    <div className="bg-white rounded-2xl shadow overflow-x-auto mb-5">
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="text-left text-gray-400 border-b border-gray-100">
                                                    <th className="p-3 font-semibold">Item</th>
                                                    <th className="p-3 font-semibold w-64">Packing</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rows.map((r, idx) => {
                                                    const { item_qty } = getBatchPackNumbers(r);
                                                    const individualInvalid = !r.units_individual || parseInt(r.units_individual, 10) <= 0;
                                                    const casesInvalid = !r.cases || parseInt(r.cases, 10) <= 0;
                                                    const perCaseInvalid = !r.units_per_case || parseInt(r.units_per_case, 10) <= 0;
                                                    const cellCls = (invalid) => `w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-none ${invalid ? "border-red-300 bg-red-50" : "border-gray-200 focus:border-teal-500"}`;
                                                    return (
                                                        <tr key={r.catalog_item_id} className="border-b border-gray-50">
                                                            <td className="p-3">
                                                                <div className="flex items-center gap-2">
                                                                    <ProductImage src={r.image_url} />
                                                                    <div className="min-w-0">
                                                                        <p className="font-semibold text-gray-800 truncate max-w-[220px]">{r.title}</p>
                                                                        <p className="text-gray-400">{r.sku}</p>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="p-3">
                                                                <div className="flex items-center gap-1.5 mb-1.5">
                                                                    <button type="button" onClick={() => updatePackMode(idx, "individual")}
                                                                        className={`px-2 py-1 rounded-lg text-[10px] font-bold border transition ${r.pack_mode === "individual" ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                                                                        Individual Units
                                                                    </button>
                                                                    <button type="button" onClick={() => updatePackMode(idx, "case")}
                                                                        className={`px-2 py-1 rounded-lg text-[10px] font-bold border transition ${r.pack_mode === "case" ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                                                                        Case Pack
                                                                    </button>
                                                                </div>
                                                                {r.pack_mode === "individual" ? (
                                                                    <div>
                                                                        <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Total Units to Ship</p>
                                                                        <input type="number" min="1" value={r.units_individual}
                                                                            onChange={(e) => updateRow(idx, "units_individual", e.target.value)}
                                                                            placeholder="e.g. 24"
                                                                            className={cellCls(individualInvalid)} />
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex items-start gap-1.5">
                                                                        <div>
                                                                            <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Cases</p>
                                                                            <input type="number" min="1" value={r.cases}
                                                                                onChange={(e) => updateRow(idx, "cases", e.target.value)}
                                                                                placeholder="e.g. 4"
                                                                                className={cellCls(casesInvalid)} style={{ width: 60 }} />
                                                                        </div>
                                                                        <span className="text-gray-300 mt-4">×</span>
                                                                        <div>
                                                                            <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Units/Case</p>
                                                                            <input type="number" min="1" value={r.units_per_case}
                                                                                onChange={(e) => updateRow(idx, "units_per_case", e.target.value)}
                                                                                placeholder="e.g. 6"
                                                                                className={cellCls(perCaseInvalid)} style={{ width: 60 }} />
                                                                        </div>
                                                                        <span className="text-[18px] text-gray-400 whitespace-nowrap mt-4">= {item_qty} units</span>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <button onClick={() => setPhase("address")} disabled={hasInvalidQty}
                                        className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition disabled:opacity-40">
                                        Continue to Preview
                                    </button>
                                </>
                            )}
                        </div>
                    )}

                    {phase === "address" && (
                        <ReturnAddressStep onBack={() => setPhase("pack")} onNext={handleAddressNext} />
                    )}
                    {phase === "preview" && shipmentId && (
                        <PreviewStep shipmentId={shipmentId} onBack={handleClose} onNext={handleConfirmed} />
                    )}

                    {phase === "handoff" && (
                        <div className="p-8 text-center">
                            <div className="w-14 h-14 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check size={26} />
                            </div>
                            <h2 className="text-xl font-bold text-gray-800 mb-2">Preview Choice Saved</h2>
                            <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                                This was a preview only — nothing has been created on Walmart yet. Your draft has been saved with this shipping method selected. To review, print labels, and actually create the shipment, finish it from <strong>Shipments → All Shipments</strong> (look for this batch's items under a draft).
                            </p>
                            <button onClick={onClose}
                                className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition mb-2">
                                Got It, Close
                            </button>
                            <button onClick={handleDiscardDraft}
                                className="text-xs text-gray-400 hover:text-red-500 font-semibold">
                                Actually, discard this draft instead
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ─── Batch Summary Sidebar ─── */
const BatchSummarySidebar = ({ batchName, batchType, products, batchId, onRemoveItem, onRetryItem, onShipAll, shipping, shipError, onPrintLabels, onCheckStatus, checkingStatus, onRefresh }) => {
    const processingCount = products.filter((p) => p.status === "processing").length;
    const totalUnits = products.reduce((sum, p) => sum + (parseInt(p.quantity, 10) || 0), 0);
    const [showFeeSettings, setShowFeeSettings] = useState(false);
    const [feeSettingsVersion, setFeeSettingsVersion] = useState(0);

    const isWFSBatch = batchType.id === "walmart_wfs";
    const [showShipmentPreview, setShowShipmentPreview] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const batchTotals = useMemo(() => products.reduce((acc, p) => {
        const costPriceEntry = (p.costs || []).find((c) => c.name?.toLowerCase() === "cost price");
        const costPriceVal = costPriceEntry ? parseFloat(costPriceEntry.value) || 0 : 0;
        const extraCostsVal = (p.costs || [])
            .filter((c) => c.name?.toLowerCase() !== "cost price")
            .reduce((s, c) => s + (parseFloat(c.value) || 0), 0);
        const salePrice = extractPriceFromPayload(p.payload) || 0;
        const { weight, length, width, height } = extractPackDetailsFromPayload(p.payload);
        const qty = Math.max(1, parseInt(p.quantity, 10) || 1);
        // Prefer the snapshot taken when this item was added to the batch
        // (persisted server-side, same for every device) over a live
        // recompute against this browser's own Fee Settings — only items
        // added before snapshotting existed fall back to the live estimate.
        const est = p.total_fees != null
            ? {
                referralFee: p.fee_breakdown?.referral_fee || 0,
                wfsFee: p.fee_breakdown?.wfs_fee || 0,
                fulfillmentCost: p.fee_breakdown?.fulfillment_cost || 0,
                inboundShipping: p.fee_breakdown?.inbound_shipping || 0,
                salesTax: p.fee_breakdown?.sales_tax || 0,
                totalFees: p.total_fees,
                totalCostBasis: costPriceVal + extraCostsVal,
                profit: salePrice - (costPriceVal + extraCostsVal) - p.total_fees,
            }
            : estimateWalmartProfit({
                salePrice, costPrice: costPriceVal, extraCosts: extraCostsVal, weight,
                dimensions: { length, width, height },
                category: p.category, subCategory: p.sub_category, isWFS: isWFSBatch,
            });
        acc.totalCost += est.totalCostBasis * qty;
        acc.totalFees += est.totalFees * qty;
        acc.totalProfit += est.profit * qty;
        acc.feeBreakdown.referral_fee += est.referralFee * qty;
        acc.feeBreakdown.wfs_fee += est.wfsFee * qty;
        acc.feeBreakdown.fulfillment_cost += est.fulfillmentCost * qty;
        acc.feeBreakdown.inbound_shipping += est.inboundShipping * qty;
        acc.feeBreakdown.sales_tax += est.salesTax * qty;
        return acc;
    }, { totalCost: 0, totalFees: 0, totalProfit: 0, feeBreakdown: { referral_fee: 0, wfs_fee: 0, fulfillment_cost: 0, inbound_shipping: 0, sales_tax: 0 } }), [products, isWFSBatch, feeSettingsVersion]);

    const batchRoi = batchTotals.totalCost > 0 ? (batchTotals.totalProfit / batchTotals.totalCost) * 100 : 0;

    // Exports the current batch's items to a CSV file — one row per item,
    // covering the fields most useful for reviewing/auditing a batch outside
    // the app (status, pricing, identifiers, error reasons for failed items).
    const exportItemsToCsv = () => {
        if (!products || products.length === 0) return;

        const headers = [
            "SKU", "Title", "Brand", "Status", "Item ID", "Feed Type", "Feed ID",
            "Price", "Quantity", "Cost Price", "Supplier", "Expiration Date",
            "Product ID Type", "Product ID", "Country of Origin", "Error Message", "Created At",
        ];

        const escapeCsvValue = (val) => {
            if (val == null) return "";
            const str = String(val);
            // Quote any value containing a comma, quote, or newline, doubling
            // internal quotes per standard CSV escaping.
            if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
            return str;
        };

        const rows = products.map((p) => {
            const orderable = p.payload?.Orderable || {};
            return [
                p.sku, p.title, p.brand, p.status, p.item_id, p.feed_type, p.feed_id,
                orderable.price, p.quantity, p.cost_price ?? "", p.supplier, p.expiration_date,
                orderable.productIdentifiers?.productIdType, orderable.productIdentifiers?.productId,
                orderable.country_of_origin_substantial_transformation,
                p.error_message, p.created_at,
            ].map(escapeCsvValue);
        });

        const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `batch_${batchName || "export"}_items.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="w-full lg:w-80 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-lg p-5 sticky top-6">
                <div className="mb-4 pb-4 border-b border-gray-100">
                    <p className="text-xs text-gray-400 font-semibold mb-0.5">BATCH</p>
                    <p className="font-bold text-gray-800 text-sm truncate">
                        {batchName?.startsWith(QUICK_BATCH_PREFIX) ? "Quick Add (no batch)" : batchName}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">{batchType.title}</p>
                    <div className="flex items-center justify-between mb-0.5">
                        <span className="text-xs font-semibold text-gray-500">{products.length} item{products.length === 1 ? "" : "s"}</span>
                        <button onClick={exportItemsToCsv} disabled={products.length === 0}
                            className="flex items-center gap-1.5 px-3 py-0.5 bg-gray-50 text-gray-600 border border-gray-200 rounded-lg text-xs font-bold hover:bg-gray-100 transition disabled:opacity-40">
                            <Download size={12} /> Export CSV
                        </button>
                    </div>
                    {products.length > 0 && (
                        <p className="text-[11px] text-gray-400 mb-2">{totalUnits} unit{totalUnits === 1 ? "" : "s"} total</p>
                    )}
                </div>

                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-gray-800 text-sm flex items-center gap-2">
                        <ClipboardList size={16} className="text-teal-600" /> Items
                    </h3>
                    <span className="bg-teal-100 text-teal-700 text-xs font-bold px-2.5 py-1 rounded-full">{products.length}</span>
                </div>

                {products.length === 0 ? (
                    <div className="text-center py-10 text-gray-400">
                        <Package size={32} className="mx-auto mb-2 opacity-40" />
                        <p className="text-xs">No items yet</p>
                    </div>
                ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto pr-1 mb-4">
                        {products.map((p) => {
                            const price = p.payload?.Orderable?.price;
                            const isDone = p.status === "processed" || p.status === "submitted"; // "submitted" kept for rows written before the backend rename

                            // Same per-unit estimate feeding batchTotals above, just
                            // computed for THIS single item so the seller can see
                            // where the batch total is coming from at a glance.
                            const costPriceEntry = (p.costs || []).find((c) => c.name?.toLowerCase() === "cost price");
                            const costPriceVal = costPriceEntry ? parseFloat(costPriceEntry.value) || 0 : 0;
                            const extraCostsVal = (p.costs || [])
                                .filter((c) => c.name?.toLowerCase() !== "cost price")
                                .reduce((s, c) => s + (parseFloat(c.value) || 0), 0);
                            const salePriceForEst = extractPriceFromPayload(p.payload) || 0;
                            const { weight, length, width, height } = extractPackDetailsFromPayload(p.payload);
                            const itemCostBasis = costPriceVal + extraCostsVal;
                            // Same snapshot-first logic as batchTotals above.
                            const itemEst = p.total_fees != null
                                ? {
                                    referralFee: p.fee_breakdown?.referral_fee || 0,
                                    wfsFee: p.fee_breakdown?.wfs_fee || 0,
                                    fulfillmentCost: p.fee_breakdown?.fulfillment_cost || 0,
                                    inboundShipping: p.fee_breakdown?.inbound_shipping || 0,
                                    salesTax: p.fee_breakdown?.sales_tax || 0,
                                    totalFees: p.total_fees,
                                    totalCostBasis: itemCostBasis,
                                    profit: salePriceForEst - itemCostBasis - p.total_fees,
                                }
                                : estimateWalmartProfit({
                                    salePrice: salePriceForEst, costPrice: costPriceVal, extraCosts: extraCostsVal, weight,
                                    dimensions: { length, width, height },
                                    category: p.category, subCategory: p.sub_category, isWFS: isWFSBatch,
                                });
                            const itemRoi = itemCostBasis > 0 ? (itemEst.profit / itemCostBasis) * 100 : 0;

                            return (
                                <div key={p.id} className="flex items-center gap-2 border border-gray-100 rounded-xl p-2">
                                    <ProductImage src={p.image_url} className="flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold text-gray-800 truncate">{p.title}</p>
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <SkuEditor item={p} batchId={batchId} onChanged={onRefresh} />
                                            {p.item_id && (
                                                <a
                                                    href={`https://www.walmart.com/ip/${p.item_id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="text-[10px] text-teal-600 hover:underline inline-flex items-center gap-0.5"
                                                >
                                                    {p.item_id} <ExternalLink size={9} />
                                                </a>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                            <PriceEditor item={p} batchId={batchId} onChanged={onRefresh} />
                                            <QuantityEditor item={p} batchId={batchId} onChanged={onRefresh} />
                                            <button
                                                onClick={() => quickPrintLabel(p)}
                                                className="text-[10px] text-teal-600 hover:text-teal-700 font-semibold inline-flex items-center gap-0.5"
                                            >
                                                <Printer size={10} /> Print
                                            </button>
                                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${isDone ? "bg-green-50 text-green-600"
                                                : p.status === "processing" ? "bg-blue-50 text-blue-600"
                                                    : p.status === "failed" ? "bg-red-50 text-red-600"
                                                        : "bg-orange-50 text-orange-600"
                                                }`}>
                                                {isDone ? "processed" : p.status}
                                                {p.status === "failed" && <ErrorPopover message={p.error_message} />}
                                            </span>
                                            {p.status === "failed" && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); onCheckStatus(); }}
                                                    disabled={checkingStatus}
                                                    title="Walmart sometimes marks items as failed, then fixes it on its own. Wait ~2 minutes, then click Recheck."
                                                    className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold underline disabled:opacity-50"
                                                >
                                                    {checkingStatus ? "Checking..." : "↻ Recheck"}
                                                </button>
                                            )}
                                        </div>
                                        {salePriceForEst > 0 && (
                                            <div className="flex items-center gap-1 mt-1 text-[9px] text-gray-400 flex-wrap">
                                                <FeeBreakdownHover est={itemEst} isWFSBatch={isWFSBatch} />
                                                <span>·</span>
                                                <span>Profit: <span className={`font-semibold ${itemEst.profit >= 0 ? "text-green-600" : "text-red-600"}`}>${itemEst.profit.toFixed(2)}</span></span>
                                                <span>·</span>
                                                <span>ROI: <span className={`font-semibold ${itemRoi >= 0 ? "text-purple-600" : "text-red-600"}`}>{itemRoi.toFixed(1)}%</span></span>
                                            </div>
                                        )}
                                    </div>
                                    {(p.status === "failed" || p.status === "processing") && (
                                        <button onClick={() => onRetryItem(p)}
                                            className="px-2 py-1 text-[10px] font-bold text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-50 transition flex-shrink-0"
                                            title="Reopen this item to fix and resubmit">
                                            Retry
                                        </button>
                                    )}
                                    <div className="flex flex-col items-center gap-1 flex-shrink-0">
                                        <button onClick={() => onRemoveItem(p.id)}
                                            className="p-1 text-gray-300 hover:text-red-500 transition" title="Remove from batch">
                                            <Trash2 size={14} />
                                        </button>
                                        {p.status === "queued" && (
                                            <button onClick={() => onRetryItem(p)}
                                                className="text-[9px] font-bold text-teal-600 hover:text-teal-700"
                                                title="Edit this item">
                                                Edit
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {processingCount > 0 && (
                    <div className="mb-2 bg-blue-50 border border-blue-100 rounded-xl p-2.5 flex items-center justify-between">
                        <span className="text-xs text-blue-700 font-semibold">
                            {processingCount} item{processingCount === 1 ? "" : "s"} processing on Walmart
                        </span>
                        <button
                            onClick={onCheckStatus}
                            disabled={checkingStatus}
                            className="text-xs text-blue-600 hover:text-blue-800 font-bold underline disabled:opacity-50"
                        >
                            {checkingStatus ? "Checking..." : "Check Status"}
                        </button>
                    </div>
                )}

                {products.length > 0 && (
                    <div className="mb-4 bg-gray-50 border border-gray-200 rounded-xl p-3">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold text-gray-600">Batch Profit Summary</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-center">
                            <div>
                                <p className="text-[10px] text-gray-400">Total Cost(s)</p>
                                <p className="font-bold text-sm text-gray-700">${batchTotals.totalCost.toFixed(2)}</p>
                            </div>
                            <div className="relative group">
                                <p className="text-[10px] text-gray-400 flex items-center justify-center gap-1 cursor-help">
                                    Est. Total Fees <span className="underline decoration-dotted">(hover)</span>
                                </p>
                                <p className="font-bold text-sm text-red-600">${batchTotals.totalFees.toFixed(2)}</p>
                                <div className="hidden group-hover:block absolute z-20 top-full left-1/2 -translate-x-1/2 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-3 w-56 text-left">
                                    <p className="text-[10px] font-bold text-gray-500 uppercase mb-1.5">Fee Breakdown (whole batch)</p>
                                    <div className="space-y-1 text-xs">
                                        <div className="flex justify-between"><span className="text-gray-500">Referral Fee</span><span className="font-semibold text-gray-700">${batchTotals.feeBreakdown.referral_fee.toFixed(2)}</span></div>
                                        {isWFSBatch ? (
                                            <>
                                                <div className="flex justify-between"><span className="text-gray-500">WFS Fee</span><span className="font-semibold text-gray-700">${batchTotals.feeBreakdown.wfs_fee.toFixed(2)}</span></div>
                                                <div className="flex justify-between"><span className="text-gray-500">Inbound Shipping</span><span className="font-semibold text-gray-700">${batchTotals.feeBreakdown.inbound_shipping.toFixed(2)}</span></div>
                                            </>
                                        ) : (
                                            <div className="flex justify-between"><span className="text-gray-500">Fulfillment Cost</span><span className="font-semibold text-gray-700">${batchTotals.feeBreakdown.fulfillment_cost.toFixed(2)}</span></div>
                                        )}
                                        <div className="flex justify-between"><span className="text-gray-500">Sales Tax (on cost)</span><span className="font-semibold text-gray-700">${batchTotals.feeBreakdown.sales_tax.toFixed(2)}</span></div>
                                        <div className="flex justify-between pt-1 border-t border-gray-100"><span className="text-gray-600 font-bold">Total</span><span className="font-bold text-red-600">${batchTotals.totalFees.toFixed(2)}</span></div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <p className="text-[10px] text-gray-400">Est. Profit</p>
                                <p className={`font-bold text-sm ${batchTotals.totalProfit >= 0 ? "text-green-600" : "text-red-600"}`}>
                                    ${batchTotals.totalProfit.toFixed(2)}
                                </p>
                            </div>
                            <div>
                                <p className="text-[10px] text-gray-400">Est. ROI</p>
                                <p className={`font-bold text-sm ${batchRoi >= 0 ? "text-purple-600" : "text-red-600"}`}>
                                    {batchRoi.toFixed(1)}%
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                {showFeeSettings && (
                    <WalmartFeeSettingsModal onClose={(saved) => {
                        setShowFeeSettings(false);
                        if (saved) setFeeSettingsVersion((v) => v + 1);
                    }} />
                )}

                <button
                    onClick={onShipAll}
                    disabled={products.filter((p) => p.status === "queued").length === 0 || shipping}
                    className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition disabled:opacity-40 flex items-center justify-center gap-2 text-sm mb-2"
                >
                    <Send size={16} /> {shipping ? "Submitting..." : "Add To Catalog"}
                </button>
                <button
                    onClick={onPrintLabels}
                    disabled={products.length === 0}
                    className="w-full py-3 bg-white border-2 border-teal-500 text-teal-600 rounded-xl font-bold hover:bg-teal-50 transition disabled:opacity-40 flex items-center justify-center gap-2 text-sm"
                >
                    🖨️ Print All Labels
                </button>
                {isWFSBatch && (
                    <button
                        onClick={() => setShowShipmentPreview(true)}
                        disabled={products.length === 0}
                        className="w-full mt-2 py-3 bg-white border-2 border-blue-400 text-blue-600 rounded-xl font-bold hover:bg-blue-50 transition disabled:opacity-40 flex items-center justify-center gap-2 text-sm"
                        title="Preview: nothing is created on Walmart — this is a read-only rate/routing check."
                    >
                        <Eye size={16} /> Show Shipment Preview
                    </button>
                )}
                {shipError && <p className="text-xs text-red-500 mt-2 text-center">{shipError}</p>}
            </div>
            {showShipmentPreview && (
                <WfsBatchPreviewModal
                    batchId={batchId}
                    products={products}
                    onClose={() => setShowShipmentPreview(false)}
                />
            )}
        </div >
    );
};

const SEARCH_TYPES = [
    { id: "title", label: "Title / Keyword" },
    { id: "upc", label: "UPC" },
    { id: "gtin", label: "GTIN" },
    { id: "item_id", label: "Item ID" },
    { id: "sku", label: "SKU" },
];

// A palette to cycle through per badge so multiple badges in the same box
// are visually distinguishable from one another.
const BADGE_COLORS = [
    "text-blue-600", "text-purple-600", "text-emerald-600",
    "text-orange-600", "text-pink-600", "text-teal-600",
];

// One single scrollable box holding every badge's text, comma-separated,
// each colored differently — always visible, no hover/click required.
const BadgesPopover = ({ badges }) => {
    if (!badges || badges.length === 0) return null;
    return (
        <span
            className="inline-flex items-center gap-1 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 max-w-[260px] overflow-x-auto whitespace-nowrap text-[10px] font-semibold"
            style={{ scrollbarWidth: "thin" }}
        >
            {badges.map((b, i) => (
                <React.Fragment key={b.id || i}>
                    <span className={BADGE_COLORS[i % BADGE_COLORS.length]}>{b.text}</span>
                    {i < badges.length - 1 && <span className="text-gray-300">,</span>}
                </React.Fragment>
            ))}
        </span>
    );
};

const CopyButton = ({ text, label }) => {
    const [copied, setCopied] = useState(false);
    return (
        <button
            onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(text);
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
            }}
            className="inline-flex items-center gap-1 hover:text-teal-600 transition"
        >
            {label}: {text} {copied ? "✓" : "⧉"}
        </button>
    );
};

/* ─── Step 3: Search Walmart's catalog ─── */
export const WalmartCatalogSearchStep = ({ onSelectExisting, onCreateNew, onBack, onImportFromBuylist, onImportFromCatalog, onImportFromCsv, initialSearchType, initialSearchValue, autoSearch }) => {
    const [searchType, setSearchType] = useState(() => initialSearchType || localStorage.getItem(LAST_SEARCH_TYPE_KEY) || "title");
    const [value, setValue] = useState(initialSearchValue || "");
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState("");
    const inputRef = useRef();

    const [enrichment, setEnrichment] = useState({});
    const enrichGenerationRef = useRef(0);
    const [visibleCount, setVisibleCount] = useState(ENRICH_PAGE_SIZE);
    const enrichedIdsRef = useRef(new Set());

    useEffect(() => {
        // Don't persist a buy-list-forced search type (e.g. UPC) as the
        // seller's own default — only remember types they picked themselves.
        if (!initialSearchType) localStorage.setItem(LAST_SEARCH_TYPE_KEY, searchType);
        inputRef.current?.focus();
    }, [searchType]);

    // Buy-list import: auto-run the search the moment this step mounts with
    // a prefilled value. The parent remounts this component (via `key`) once
    // per queued item, so this fires exactly once per item, never again.
    useEffect(() => {
        if (autoSearch && initialSearchValue) {
            handleSearch(initialSearchValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!results || results.length === 0) return;
        const generation = ++enrichGenerationRef.current;
        let cancelled = false;

        const toEnrich = results.slice(0, visibleCount).filter((item) => !enrichedIdsRef.current.has(item.item_id));
        toEnrich.forEach((item) => enrichedIdsRef.current.add(item.item_id));

        (async () => {
            for (const item of toEnrich) {
                if (cancelled || enrichGenerationRef.current !== generation) return;
                await enrichItem(item.item_id, generation);
                await new Promise((r) => setTimeout(r, ENRICH_STAGGER_MS));
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results, visibleCount]);

    const enrichItem = async (itemId, generation) => {
        setEnrichment((e) => ({ ...e, [itemId]: { ...(e[itemId] || {}), loading: true, error: null } }));
        try {
            const resp = await api(`/api/walmart/basic/${encodeURIComponent(itemId)}`);
            const data = await resp.json();
            if (enrichGenerationRef.current !== generation) return;
            if (!resp.ok || data.error) throw new Error(data.error || "Lookup failed");
            setEnrichment((e) => ({
                ...e,
                [itemId]: {
                    loading: false,
                    upc: data.upc || null,
                    gtin: data.gtin || null,
                    weight: data.weight || null,
                    price: data.price ?? null,
                    image: data.image || null,
                    total_variations: data.total_variations || null,
                    rating: data.rating || null,
                    review_count: data.review_count || null,
                    badges: data.badges || [],
                    category: data.category || null,
                    sub_category: data.sub_category || null,
                },
            }));
        } catch (err) {
            if (enrichGenerationRef.current !== generation) return;
            setEnrichment((e) => ({ ...e, [itemId]: { loading: false, error: true } }));
        }
    };

    const handleSearch = async (overrideValue) => {
        const searchValue = overrideValue ?? value;
        if (!searchValue.trim() || loading) return;
        setLoading(true);
        setError("");
        setResults(null);
        setEnrichment({});
        setVisibleCount(ENRICH_PAGE_SIZE);
        enrichedIdsRef.current = new Set();
        try {
            if (searchType === "item_id") {
                const trimmed = searchValue.trim();
                // Per your instruction: go straight to inventory-basic, no text search.
                const invResp = await api(`/api/walmart/inventory-basic/${encodeURIComponent(trimmed)}`);
                const inv = await invResp.json();
                if (!invResp.ok || inv.error) throw new Error(inv.error || "Item lookup failed for that Item ID");

                // inventory-basic has no productType field — pull it from the
                // existing /item endpoint (ITEM_ID lookup), a single attempt
                // (the api() wrapper already retries under the hood, so no
                // extra loop here).
                let productType = null;
                try {
                    const ptResp = await api(`/api/walmart/api/item?product_id=${encodeURIComponent(trimmed)}&product_id_type=ITEM_ID`);
                    const ptData = await ptResp.json();
                    productType = ptData?.ItemResponse?.[0]?.productType || null;
                } catch { /* falls through to the UPC/GTIN fallback below */ }

                if (!productType) {
                    // ITEM_ID lookup failed — Walmart's ITEM_ID endpoint 404s
                    // for plenty of real items even though inventory-basic
                    // (scraping) found them fine. Fall back to actually
                    // running a catalog search by the UPC/GTIN we already
                    // scraped, instead of just prefilling and waiting for a
                    // click — switch the tab for visual clarity, but drive
                    // the search ourselves via the same catalog-search
                    // endpoint the UPC/GTIN tabs normally use.
                    // Prefer UPC/GTIN when we scraped one (an exact-match
                    // lookup). If we didn't get either, fall back further to
                    // a keyword search on the item's own scraped title —
                    // still a real Walmart-wide catalog search (v3/items/
                    // walmart/search with `query`), just less precise than
                    // an exact ID match. This is what lets Item ID search
                    // succeed for a product that isn't in the SELLER's own
                    // catalog at all, which is the whole point of this
                    // fallback chain.
                    const fallbackId = inv.upc || inv.gtin;
                    const fallbackType = inv.upc ? "upc" : inv.gtin ? "gtin" : (inv.name ? "title" : null);
                    const fallbackValue = fallbackId || inv.name;

                    if (!fallbackType || !fallbackValue) {
                        setError("Couldn't find this item by Item ID, UPC/GTIN, or title.");
                        setLoading(false);
                        inputRef.current?.focus();
                        return;
                    }

                    setSearchType(fallbackType);
                    setValue(fallbackValue);

                    const fbResp = await api("/api/walmart/api/catalog-search", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ type: fallbackType, value: fallbackValue }),
                    });
                    const fbData = await fbResp.json();
                    if (!fbResp.ok) throw new Error(fbData.error || `Request failed: ${fbResp.status}`);
                    setResults(fbData.results || []);
                    setLoading(false);
                    inputRef.current?.focus();
                    return;
                }

                setResults([{
                    item_id: inv.item_id || trimmed,
                    title: inv.name,
                    brand: inv.brand,
                    description: inv.short_description || inv.long_description,
                    image_url: inv.image,
                    price: inv.price,
                    product_type: productType,
                    category: inv.category || null,
                    sub_category: inv.sub_category || null,
                    upc: inv.upc || null,
                    gtin: inv.gtin || null,
                    searched_upc: inv.upc || null,
                    searched_gtin: inv.gtin || null,
                    condition: null,
                    raw: inv,
                }]);
            } else if (searchType === "sku") {
                const resp = await api(`/api/walmart/api/item?product_id=${encodeURIComponent(searchValue.trim())}&product_id_type=SKU`);
                const data = await resp.json();
                if (!resp.ok) throw new Error(data.error || `Request failed: ${resp.status}`);
                const it = data.ItemResponse?.[0];
                if (!it) throw new Error("No item found for that SKU");

                setResults([{
                    item_id: it.wpid,
                    title: it.productName,
                    brand: it.brand,
                    price: it.price?.amount,
                    currency: it.price?.currency || "USD",
                    product_type: it.productType,
                    category: null,
                    sub_category: null,
                    upc: it.upc || null,
                    gtin: it.gtin || null,
                    searched_upc: it.upc || null,
                    searched_gtin: it.gtin || null,
                    is_marketplace_item: it.isMarketPlaceItem,
                    condition: it.condition,
                    sku: it.sku,
                    raw: it,
                }]);

                // /v3/items/{id} (SKU lookup) has no image/rating/badges/etc at
                // all — backfill everything from /api/walmart/basic, the SAME
                // lookup already used for UPC/GTIN enrichment on the title
                // search path, so this result renders through the identical
                // enrichment-driven UI (image, price, rating, badges, ...).
                // Bypasses enrichItem's generation-tracking on purpose: that
                // mechanism is designed around the useEffect that watches
                // `results`, and calling it here would race against the
                // generation bump that same effect triggers when setResults
                // above causes a re-render.
                if (it.wpid) {
                    enrichedIdsRef.current.add(it.wpid); // tells the generic effect to skip re-fetching this one
                    setEnrichment((e) => ({ ...e, [it.wpid]: { ...(e[it.wpid] || {}), loading: true, error: null } }));
                    try {
                        const basicResp = await api(`/api/walmart/basic/${encodeURIComponent(it.wpid)}`);
                        const basic = await basicResp.json();
                        if (!basicResp.ok || basic.error) throw new Error(basic.error || "Lookup failed");
                        setEnrichment((e) => ({
                            ...e,
                            [it.wpid]: {
                                loading: false,
                                upc: basic.upc || null,
                                gtin: basic.gtin || null,
                                weight: basic.weight || null,
                                price: basic.price ?? null,
                                image: basic.image || null,
                                total_variations: basic.total_variations || null,
                                rating: basic.rating || null,
                                review_count: basic.review_count || null,
                                badges: basic.badges || [],
                                category: basic.category || null,
                                sub_category: basic.sub_category || null,
                            },
                        }));
                    } catch {
                        setEnrichment((e) => ({ ...e, [it.wpid]: { loading: false, error: true } }));
                    }
                }
            } else {
                const resp = await api("/api/walmart/api/catalog-search", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ type: searchType, value: searchValue.trim() }),
                });
                const data = await resp.json();
                if (!resp.ok) throw new Error(data.error || `Request failed: ${resp.status}`);
                setResults(data.results || []);
            }
        } catch (err) {
            setError(err.message || "Search failed. Please try again.");
        } finally {
            setLoading(false);
            inputRef.current?.focus();
        }
    };

    return (
        <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-8">
                <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition">
                    <ChevronLeft size={20} />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Find a Product</h1>
                    <p className="text-gray-500 text-sm">Search Walmart's catalog to add an item to this batch</p>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 mb-5">
                <div className="flex gap-2 mb-4 flex-wrap">
                    {SEARCH_TYPES.map((t) => (
                        <button key={t.id} onClick={() => setSearchType(t.id)}
                            className={`px-4 py-2 text-sm font-bold rounded-lg border-2 transition ${searchType === t.id ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}>
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="flex gap-2">
                    <input
                        ref={inputRef}
                        value={value}
                        onChange={(e) => {
                            const next = e.target.value;
                            setValue(next);
                            const looksLikeBarcode = /^\d{12,14}$/.test(next.trim());
                            if (looksLikeBarcode && (searchType === "upc" || searchType === "gtin")) handleSearch(next);
                        }}
                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                        placeholder={
                            searchType === "title" ? "e.g. Stainless Steel Water Bottle 32oz" :
                                searchType === "upc" ? "12-digit UPC" :
                                    searchType === "gtin" ? "14-digit GTIN" :
                                        searchType === "sku" ? "Your SKU" : "Walmart Item ID"
                        }
                        className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-500 font-mono"
                        autoFocus
                    />
                    <button onClick={() => handleSearch()} disabled={loading || !value.trim()}
                        className="px-5 py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition disabled:opacity-50">
                        {loading ? <Loader size={18} className="animate-spin" /> : <Search size={18} />}
                    </button>
                </div>

                {error && (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-700 text-sm">
                        <AlertCircle size={16} /> {error}
                    </div>
                )}
            </div>

            {results !== null && results.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-6 mb-5">
                    <h2 className="font-bold text-gray-800 mb-4">{results.length} result{results.length === 1 ? "" : "s"}</h2>

                    <div className="space-y-2 overflow-y-auto pr-1" style={{ maxHeight: "468px" }}>
                        {results.slice(0, visibleCount).map((item) => {
                            const e = enrichment[item.item_id] || {};
                            const displayImage = e.image || item.image_url;
                            const displayPrice = e.price ?? item.price;
                            const displayRating = e.rating ?? item.customer_rating;
                            const displayReviewCount = e.review_count ?? item.num_reviews;
                            return (
                                <div key={item.item_id} className="flex items-start gap-3 border border-gray-100 hover:border-teal-200 rounded-xl p-3 transition">
                                    <ProductImage src={displayImage} className="flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-gray-800 truncate">{item.title}</p>
                                        <p className="text-xs text-gray-400 flex items-center gap-1 flex-wrap">
                                            {item.brand ? `${item.brand} · ` : ""}
                                            <a href={`https://www.walmart.com/ip/${item.item_id}`} target="_blank" rel="noopener noreferrer"
                                                onClick={(ev) => ev.stopPropagation()}
                                                className="text-teal-600 hover:underline inline-flex items-center gap-0.5">
                                                {item.item_id} <ExternalLink size={10} />
                                            </a>
                                            {displayPrice != null ? ` · $${displayPrice}` : ""}
                                        </p>
                                        <div className="flex items-center gap-1 text-[10px] text-gray-400 mt-1">
                                            {e.loading ? "Looking up UPC..." : e.upc ? <CopyButton text={e.upc} label="UPC" /> : e.gtin ? <CopyButton text={e.gtin} label="GTIN" /> : e.error ? (
                                                <button onClick={(ev) => { ev.stopPropagation(); enrichItem(item.item_id, enrichGenerationRef.current); }} className="text-orange-500 hover:text-orange-600">
                                                    UPC lookup failed ↻
                                                </button>
                                            ) : null}
                                        </div>
                                        <div className="flex gap-2 mt-1 flex-wrap items-center">
                                            {displayRating != null && (
                                                <span className="text-[10px] text-gray-400">
                                                    ⭐ {displayRating}{displayReviewCount ? ` (${displayReviewCount})` : ""}
                                                </span>
                                            )}
                                            {item.condition && <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-semibold">{item.condition}</span>}
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${item.is_marketplace_item ? "bg-blue-50 text-blue-600" : "bg-orange-50 text-orange-600"}`}>
                                                {item.is_marketplace_item ? "Walmart is not the seller" : "Sold by Walmart"}
                                            </span>
                                            {e.total_variations > 1 && (
                                                <span className="text-[10px] bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded font-semibold">
                                                    {e.total_variations} variations
                                                </span>
                                            )}
                                            <BadgesPopover badges={e.badges} />
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => onSelectExisting({
                                            ...item,
                                            upc: e.upc || item.upc || null,
                                            gtin: e.gtin || item.gtin || null,
                                            scraped_weight: e.weight || null,
                                            image_url: displayImage,
                                            price: displayPrice,
                                            category: e.category || item.category || null,
                                            sub_category: e.sub_category || item.sub_category || null,
                                        })}
                                        className="px-4 py-2 bg-teal-600 text-white rounded-lg text-xs font-bold hover:bg-teal-700 transition flex-shrink-0"
                                    >
                                        Use This Item
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    {visibleCount < results.length && (
                        <button onClick={() => setVisibleCount((c) => c + ENRICH_PAGE_SIZE)}
                            className="w-full mt-3 py-2.5 border-2 border-dashed border-gray-300 hover:border-teal-400 rounded-xl text-gray-500 hover:text-teal-600 text-sm font-semibold transition">
                            Load 10 More ({results.length - visibleCount} remaining)
                        </button>
                    )}
                </div>
            )}

            {results !== null && results.length === 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-6 mb-5">
                    <p className="text-sm text-gray-400">No matches in Walmart's catalog. Use "Create New Listing" below.</p>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <button onClick={onCreateNew}
                    className="w-full py-4 px-1 border-2 border-dashed border-gray-300 hover:border-teal-400 rounded-2xl text-gray-500 hover:text-teal-600 font-semibold transition flex items-center justify-center gap-2">
                    <Sparkles size={32} /> Can't find it? Create New Listing
                </button>
                {onImportFromBuylist && (
                    <button onClick={onImportFromBuylist}
                        className="w-full py-4 px-1 border-2 border-dashed border-purple-300 hover:border-purple-400 rounded-2xl text-purple-500 hover:text-purple-600 font-semibold transition flex items-center justify-center gap-2">
                        📋 Import from Buy List
                    </button>
                )}
                {onImportFromCatalog && (
                    <button onClick={onImportFromCatalog}
                        className="w-full py-4 px-1 border-2 border-dashed border-blue-300 hover:border-blue-400 rounded-2xl text-blue-500 hover:text-blue-600 font-semibold transition flex items-center justify-center gap-2">
                        🗂️ Add from My Catalog
                    </button>
                )}
                {onImportFromCsv && (
                    <button onClick={onImportFromCsv}
                        className="w-full py-4 px-1 border-2 border-dashed border-green-300 hover:border-green-400 rounded-2xl text-green-600 hover:text-green-700 font-semibold transition flex items-center justify-center gap-2">
                        📄 Import from CSV
                    </button>
                )}
            </div>
        </div>
    );
};

const BUYLIST_PLATFORM_COLORS = { amazon: '#FF9900', ebay: '#E53238', walmart: '#0071CE', aip: '#8B5CF6', general: '#10b981' };

const extractBuylistImageUrl = (raw) => {
    if (!raw) return '';
    const s = String(raw);
    const match = s.match(/=IMAGE\("([^"]+)"\)/i);
    if (match) return match[1];
    if (s.startsWith('http') || s.startsWith('data:')) return s;
    return '';
};

const findBuylistHeaderValue = (headers, row, partials) => {
    for (const p of partials) {
        const idx = headers.findIndex((h) => h.toLowerCase().includes(p.toLowerCase()));
        if (idx !== -1 && row[idx]) return row[idx];
    }
    return '';
};

/* ─── Buy List picker: browse your buy lists, select items, hand them off
   to BatchesPage as a queue to walk through the normal new-listing flow. ─── */
export const BuylistImportStep = ({ onImportSelected, onBack }) => {
    const [allLists, setAllLists] = useState(null);
    const [currentListName, setCurrentListName] = useState('');
    const [currentPlatform, setCurrentPlatform] = useState('');
    const [viewData, setViewData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(new Set());
    const [showSwitcher, setShowSwitcher] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const result = await authService.getBuyListNames('all');
            if (result.success && result.listNames.length > 0) {
                setAllLists(result.listNames);
                const first = result.listNames[0];
                setCurrentListName(first.name);
                setCurrentPlatform(first.platform);
                await loadList(first.name, first.platform);
            } else {
                setAllLists([]);
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadList = async (name, platform) => {
        setLoading(true);
        setSelected(new Set());
        const result = await authService.exportBuyList(name, platform);
        if (result.success) setViewData(result.csvData);
        else setViewData([]);
        setLoading(false);
    };

    const switchList = async (name, platform) => {
        setCurrentListName(name);
        setCurrentPlatform(platform);
        setShowSwitcher(false);
        setViewData([]);
        await loadList(name, platform);
    };

    const headers = viewData.length > 0 ? viewData[0] : [];
    const dataRows = viewData.length > 1 ? viewData.slice(1) : [];

    const filteredRows = dataRows
        .map((row, i) => ({ row, i }))
        .filter(({ row }) => !search.trim() || row.some((c) => String(c).toLowerCase().includes(search.toLowerCase())));

    const toggle = (i) => setSelected((prev) => {
        const next = new Set(prev);
        next.has(i) ? next.delete(i) : next.add(i);
        return next;
    });

    const toggleAll = () => {
        if (selected.size === filteredRows.length) setSelected(new Set());
        else setSelected(new Set(filteredRows.map(({ i }) => i)));
    };

    const buildItemsAndContinue = () => {
        const imageHeader = headers.find((h) => h.toLowerCase().includes('image'));
        const items = [...selected].map((i) => {
            const row = dataRows[i];
            const image_url = imageHeader ? extractBuylistImageUrl(row[headers.indexOf(imageHeader)]) : '';
            const upc = findBuylistHeaderValue(headers, row, ['upc']);
            const title = findBuylistHeaderValue(headers, row, ['name', 'title']);
            const notes = findBuylistHeaderValue(headers, row, ['notes']);
            // Full row, header->value, for the "View saved data" popup later —
            // keeps every column from the buy list, not just the 4 we act on.
            const allFields = headers.map((h, idx) => ({ label: h, value: row[idx] })).filter((f) => f.value);
            return { upc, title, description: notes, image_url, allFields };
        }).filter((it) => it.upc || it.title);

        onImportSelected(items);
    };

    return (
        <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-8">
                <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition">
                    <ChevronLeft size={20} />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Import from Buy List</h1>
                    <p className="text-gray-500 text-sm">Select items from your buy list to add to this batch</p>
                </div>
            </div>

            {loading && allLists === null && <p className="text-center text-gray-400 text-sm py-10">Loading your buy lists...</p>}
            {allLists !== null && allLists.length === 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-8 text-center text-gray-400 text-sm">
                    No buy lists found. Add items to a buy list from the main app first.
                </div>
            )}

            {allLists !== null && allLists.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-5 mb-5">
                    <div className="flex items-center gap-3 mb-4 flex-wrap">
                        <div className="relative">
                            <button onClick={() => setShowSwitcher((o) => !o)}
                                className="flex items-center gap-2 px-3 py-2 border-2 border-gray-200 rounded-lg text-sm font-bold text-gray-700 hover:border-teal-400 transition">
                                {currentListName || "Select list"}
                                <span style={{ background: BUYLIST_PLATFORM_COLORS[currentPlatform] || '#6b7280' }}
                                    className="text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase">{currentPlatform}</span>
                                ▾
                            </button>
                            {showSwitcher && (
                                <div className="absolute z-20 top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg min-w-[220px] max-h-72 overflow-y-auto">
                                    {allLists.map((item) => (
                                        <button key={`${item.name}__${item.platform}`} onClick={() => switchList(item.name, item.platform)}
                                            className="w-full text-left px-4 py-2.5 text-sm hover:bg-teal-50 flex items-center justify-between border-b border-gray-50">
                                            <span className={item.name === currentListName && item.platform === currentPlatform ? "font-bold text-teal-700" : "text-gray-700"}>{item.name}</span>
                                            <span style={{ background: BUYLIST_PLATFORM_COLORS[item.platform] || '#6b7280' }}
                                                className="text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase">{item.platform}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search this list..."
                            className="flex-1 min-w-[160px] border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
                        <button onClick={toggleAll} className="text-xs font-bold text-teal-600 hover:text-teal-700">
                            {selected.size === filteredRows.length && filteredRows.length > 0 ? "Deselect All" : "Select All"}
                        </button>
                        <span className="text-xs text-gray-400 font-semibold">{selected.size} selected</span>
                    </div>

                    {loading ? (
                        <p className="text-center text-gray-400 text-sm py-10">Loading items...</p>
                    ) : filteredRows.length === 0 ? (
                        <p className="text-center text-gray-400 text-sm py-10">No items in this list.</p>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto pr-1">
                            {filteredRows.map(({ row, i }) => {
                                const imageHeader = headers.find((h) => h.toLowerCase().includes('image'));
                                const imageUrl = imageHeader ? extractBuylistImageUrl(row[headers.indexOf(imageHeader)]) : '';
                                const title = findBuylistHeaderValue(headers, row, ['product name']) || findBuylistHeaderValue(headers, row, ['name', 'title']) || '(No title)';
                                const upc = findBuylistHeaderValue(headers, row, ['upc']);
                                const isChecked = selected.has(i);
                                return (
                                    <label key={i} className={`flex items-center gap-2 border rounded-xl p-2 cursor-pointer transition ${isChecked ? "border-teal-400 bg-teal-50" : "border-gray-100 hover:border-gray-200"}`}>
                                        <input type="checkbox" checked={isChecked} onChange={() => toggle(i)} className="w-4 h-4 accent-teal-600 flex-shrink-0" />
                                        <ProductImage src={imageUrl} className="flex-shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-xs font-semibold text-gray-800 truncate">{title}</p>
                                            {upc && <p className="text-[10px] text-gray-400 font-mono">UPC {upc}</p>}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            <button onClick={buildItemsAndContinue} disabled={selected.size === 0}
                className="w-full py-4 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-2xl font-bold hover:opacity-90 transition disabled:opacity-40 flex items-center justify-center gap-2">
                Add {selected.size || ""} Selected Item{selected.size === 1 ? "" : "s"} to Batch
            </button>
        </div>
    );
};

// Maps a catalog_items row into the same shape WalmartCatalogSearchStep's
// own results already produce (item_id/title/brand/price/product_type/
// upc/gtin/searched_upc/searched_gtin/condition/raw) — so it can be handed
// straight to handleSelectExisting exactly like a Walmart search result,
// with no extra Walmart lookup needed since our own catalog row already
// has everything (title, image, price, product type, identifiers).
const mapCatalogItemToProduct = (item) => ({
    item_id: item.wpid || "",
    title: item.title,
    brand: item.brand,
    description: item.description,
    image_url: item.image_url,
    price: item.price,
    product_type: item.product_type,
    category: item.category || null,
    sub_category: item.sub_category || null,
    upc: item.upc,
    gtin: item.gtin,
    searched_upc: item.upc,
    searched_gtin: item.gtin,
    condition: null,
    raw: item,
});

const CATALOG_PICK_PAGE_SIZE = 30;

const CSV_ID_TYPES = [
    { id: "upc", label: "UPC" },
    { id: "gtin", label: "GTIN" },
    { id: "sku", label: "SKU" },
    { id: "item_id", label: "Item ID" },
    { id: "title", label: "Title / Keyword" },
];

/* ─── CSV import: upload any CSV, pick which column is the identifier and
   what type it is, then queue those items through the normal "Find a
   Product" search flow one at a time (same idea as the Buy List queue) —
   a CSV only ever gives us an identifier, never full item data, so each
   row still needs its own Walmart lookup before it can be added. ─── */
const CsvImportStep = ({ onImportSelected, onBack }) => {
    const [headers, setHeaders] = useState([]);
    const [rows, setRows] = useState([]);
    const [idColumn, setIdColumn] = useState("");
    const [idType, setIdType] = useState("upc");
    const [titleColumn, setTitleColumn] = useState("");
    const [error, setError] = useState("");
    const [fileName, setFileName] = useState("");

    const handleFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setError("");
        setFileName(file.name);
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (result) => {
                const cols = result.meta.fields || [];
                if (cols.length === 0) { setError("Couldn't read any columns from this file."); return; }
                setHeaders(cols);
                setRows(result.data || []);
                // Best-effort guess at the identifier column by name.
                const guess = cols.find((c) => /upc/i.test(c)) || cols.find((c) => /gtin/i.test(c))
                    || cols.find((c) => /sku/i.test(c)) || cols[0];
                setIdColumn(guess);
                const titleGuess = cols.find((c) => /title|name/i.test(c));
                if (titleGuess) setTitleColumn(titleGuess);
            },
            error: () => setError("Failed to parse this CSV file."),
        });
    };

    const validRows = rows.filter((r) => idColumn && String(r[idColumn] || "").trim());

    const handleImport = () => {
        const items = validRows.map((r) => ({
            idType,
            idValue: String(r[idColumn]).trim(),
            title: titleColumn ? String(r[titleColumn] || "").trim() : "",
        }));
        onImportSelected(items);
    };

    return (
        <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-8">
                <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition">
                    <ChevronLeft size={20} />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Import from CSV</h1>
                    <p className="text-gray-500 text-sm">Upload any CSV — pick which column holds your item identifier</p>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-5 mb-5">
                <label className="block border-2 border-dashed border-gray-300 hover:border-teal-400 rounded-xl p-6 text-center cursor-pointer transition mb-4">
                    <input type="file" accept=".csv" onChange={handleFile} className="hidden" />
                    <p className="text-sm font-semibold text-gray-600">{fileName || "Click to upload a CSV file"}</p>
                    <p className="text-xs text-gray-400 mt-1">Any CSV works — you'll map the columns next</p>
                </label>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-xs text-red-700 flex items-center gap-2">
                        <AlertCircle size={14} /> {error}
                    </div>
                )}

                {headers.length > 0 && (
                    <>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div>
                                <label className="text-xs font-semibold text-gray-600 mb-1 block">Identifier Column</label>
                                <select value={idColumn} onChange={(e) => setIdColumn(e.target.value)}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 bg-white">
                                    {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-600 mb-1 block">Identifier Type</label>
                                <div className="flex gap-1.5 flex-wrap">
                                    {CSV_ID_TYPES.map((t) => (
                                        <button key={t.id} type="button" onClick={() => setIdType(t.id)}
                                            className={`px-3 py-1.5 text-xs font-bold rounded-lg border-2 transition ${idType === t.id ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 text-gray-500"}`}>
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="mb-4">
                            <label className="text-xs font-semibold text-gray-600 mb-1 block">Title Column (optional, helps AI/search)</label>
                            <select value={titleColumn} onChange={(e) => setTitleColumn(e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 bg-white">
                                <option value="">None</option>
                                {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                            </select>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-3 mb-4 text-xs text-gray-500">
                            <span className="font-bold text-gray-700">{validRows.length}</span> of {rows.length} rows have a value in the identifier column and will be imported.
                        </div>

                        <div className="max-h-48 overflow-y-auto border border-gray-100 rounded-xl mb-4">
                            <table className="w-full text-xs">
                                <thead><tr className="text-left text-gray-400 border-b border-gray-100"><th className="p-2">{idColumn}</th>{titleColumn && <th className="p-2">{titleColumn}</th>}</tr></thead>
                                <tbody>
                                    {validRows.slice(0, 8).map((r, i) => (
                                        <tr key={i} className="border-b border-gray-50">
                                            <td className="p-2 font-mono">{r[idColumn]}</td>
                                            {titleColumn && <td className="p-2 truncate max-w-[240px]">{r[titleColumn]}</td>}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            <button onClick={handleImport} disabled={validRows.length === 0}
                className="w-full py-4 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-2xl font-bold hover:opacity-90 transition disabled:opacity-40 flex items-center justify-center gap-2">
                Import {validRows.length || ""} Item{validRows.length === 1 ? "" : "s"} from CSV
            </button>
        </div>
    );
};

/* ─── Catalog picker: browse your OWN synced catalog (not Walmart's global
   search), select items, hand them straight to the details flow — since a
   catalog row already has title/image/price/product type/identifiers, this
   skips the "Find a Product" search step entirely. ─── */
const CatalogImportStep = ({ onImportSelected, onBack }) => {
    const [items, setItems] = useState(null);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(new Set());

    const runSearch = async () => {
        setLoading(true);
        try {
            const d = await listCatalog({ marketplace: "walmart", search: search.trim() || undefined, page: 1, page_size: CATALOG_PICK_PAGE_SIZE });
            setItems(d.items || []);
            setTotal(d.total || 0);
            setPage(1);
            setSelected(new Set());
        } catch {
            setItems([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    const loadMore = async () => {
        if (loadingMore || !items || items.length >= total) return;
        setLoadingMore(true);
        try {
            const nextPage = page + 1;
            const d = await listCatalog({ marketplace: "walmart", search: search.trim() || undefined, page: nextPage, page_size: CATALOG_PICK_PAGE_SIZE });
            setItems((prev) => [...prev, ...(d.items || [])]);
            setTotal(d.total || 0);
            setPage(nextPage);
        } finally {
            setLoadingMore(false);
        }
    };

    useEffect(() => { runSearch(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = (id) => setSelected((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    const toggleAll = () => {
        const visible = items || [];
        if (selected.size === visible.length && visible.length > 0) setSelected(new Set());
        else setSelected(new Set(visible.map((it) => it.id)));
    };

    const handleAdd = () => {
        const chosen = (items || []).filter((it) => selected.has(it.id));
        onImportSelected(chosen);
    };

    return (
        <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-8">
                <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition">
                    <ChevronLeft size={20} />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Add from My Catalog</h1>
                    <p className="text-gray-500 text-sm">Pick items already in your catalog to add to this batch</p>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-5 mb-5">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <input value={search} onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && runSearch()}
                        placeholder="Search by title, SKU, or UPC..."
                        className="flex-1 min-w-[200px] border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
                    <button onClick={runSearch} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-bold hover:bg-teal-700 transition">
                        Search
                    </button>
                    <button onClick={toggleAll} className="text-xs font-bold text-teal-600 hover:text-teal-700">
                        {selected.size > 0 && selected.size === (items || []).length ? "Deselect All" : "Select All Loaded"}
                    </button>
                    <span className="text-xs text-gray-400 font-semibold">
                        {selected.size} selected · {items ? items.length : 0} of {total} loaded
                    </span>
                </div>

                {loading ? (
                    <p className="text-center text-gray-400 text-sm py-10">Loading...</p>
                ) : (items || []).length === 0 ? (
                    <p className="text-center text-gray-400 text-sm py-10">No matching items in your catalog.</p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto pr-1">
                        {items.map((it) => {
                            const isChecked = selected.has(it.id);
                            return (
                                <label key={it.id} className={`flex items-center gap-2 border rounded-xl p-2 cursor-pointer transition ${isChecked ? "border-teal-400 bg-teal-50" : "border-gray-100 hover:border-gray-200"}`}>
                                    <input type="checkbox" checked={isChecked} onChange={() => toggle(it.id)} className="w-4 h-4 accent-teal-600 flex-shrink-0" />
                                    {it.image_url ? (
                                        <img src={it.image_url} alt="" className="w-11 h-11 object-contain rounded flex-shrink-0" />
                                    ) : (
                                        <div className="w-11 h-11 flex items-center justify-center text-2xl flex-shrink-0">📦</div>
                                    )}
                                    <div className="min-w-0">
                                        <p className="text-xs font-semibold text-gray-800 truncate">{it.title || "Untitled"}</p>
                                        <p className="text-[10px] text-gray-400 font-mono">{it.sku}{it.price != null ? ` · $${it.price}` : ""}</p>
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                )}

                {items && items.length < total && (
                    <button onClick={loadMore} disabled={loadingMore}
                        className="w-full mt-3 py-2.5 border-2 border-dashed border-gray-300 hover:border-teal-400 rounded-xl text-gray-500 hover:text-teal-600 text-sm font-semibold transition disabled:opacity-50">
                        {loadingMore ? "Loading..." : `Load ${CATALOG_PICK_PAGE_SIZE} More (${total - items.length} remaining)`}
                    </button>
                )}
            </div>

            <button onClick={handleAdd} disabled={selected.size === 0}
                className="w-full py-4 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-2xl font-bold hover:opacity-90 transition disabled:opacity-40 flex items-center justify-center gap-2">
                Add {selected.size || ""} Selected Item{selected.size === 1 ? "" : "s"} to Batch
            </button>
        </div>
    );
};

// Popup listing every saved field for the current buy-list item, each with
// its own copy button — lets the seller pull data (cost, source, ASIN,
// whatever the buy list captured) into the form fields by hand as needed.
const BuylistItemDataModal = ({ item, onClose }) => {
    const [copiedLabel, setCopiedLabel] = useState(null);
    const [search, setSearch] = useState("");

    const copy = (label, value) => {
        navigator.clipboard.writeText(String(value));
        setCopiedLabel(label);
        setTimeout(() => setCopiedLabel((cur) => (cur === label ? null : cur)), 1200);
    };

    const fields = item?.allFields || [];
    const query = search.trim().toLowerCase();
    const filteredFields = query
        ? fields.filter((f) => f.label.toLowerCase().includes(query) || String(f.value).toLowerCase().includes(query))
        : fields;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="bg-purple-50 border-b border-purple-100 px-5 py-3 flex items-center justify-between flex-shrink-0">
                    <h3 className="font-bold text-purple-700 text-sm flex items-center gap-2">
                        📋 Saved Buy List Data
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                </div>
                <div className="px-4 pt-3 pb-1 flex-shrink-0">
                    <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search field or value (e.g. cost)..."
                            className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2 text-xs focus:outline-none focus:border-purple-400"
                            autoFocus
                        />
                    </div>
                </div>
                <div className="p-4 pt-2 overflow-y-auto flex-1">
                    {fields.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-6">No saved data found for this item.</p>
                    ) : filteredFields.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-6">No fields match "{search}".</p>
                    ) : (
                        <div className="space-y-2">
                            {filteredFields.map((f) => (
                                <div key={f.label} className="border border-gray-100 rounded-lg p-2.5">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-0.5">{f.label}</p>
                                    <div className="flex items-center justify-between gap-2">
                                        <p className="text-sm text-gray-800 break-words flex-1">{String(f.value)}</p>
                                        <button
                                            onClick={() => copy(f.label, f.value)}
                                            className="flex-shrink-0 text-xs font-bold text-purple-600 hover:text-purple-800 inline-flex items-center gap-1"
                                        >
                                            {copiedLabel === f.label ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ─── Main BatchesPage ─── */
const BatchesPage = ({ quickAddItemId } = {}) => {
    const [flowStep, setFlowStep] = useState("resume"); // resume | type | name | search | buylist_pick | details
    const [batchType, setBatchType] = useState(null);
    const [batch, setBatch] = useState(null);
    const [products, setProducts] = useState([]);
    const [pendingItem, setPendingItem] = useState(null);
    const [retryItem, setRetryItem] = useState(null);
    const [shipping, setShipping] = useState(false);
    const [shipError, setShipError] = useState("");
    const [showLabelsModal, setShowLabelsModal] = useState(false);

    // Buy List bulk-import queue — selecting N items lands on the normal
    // "Find a Product" search step, once per item, with that item's UPC
    // pre-filled and auto-searched against Walmart's own catalog. Adding
    // the matched (or manually created) item to the batch automatically
    // advances to the next queued item.
    const [buylistCurrentItem, setBuylistCurrentItem] = useState(null); // { upc, title, description, image_url, allFields } | null
    const [buylistQueue, setBuylistQueue] = useState([]);
    const [buylistQueueTotal, setBuylistQueueTotal] = useState(0);
    const [buylistItemKey, setBuylistItemKey] = useState(0); // forces WalmartCatalogSearchStep to remount per item
    const [showBuylistDataModal, setShowBuylistDataModal] = useState(false);

    // Catalog import queue — same one-at-a-time idea as the buy-list queue
    // above, but each item already has full data (title/image/price/
    // product type/identifiers) straight from our own catalog_items table,
    // so it skips "Find a Product" entirely and lands directly on Item
    // Details for each selected item in turn.
    const [catalogCurrentItem, setCatalogCurrentItem] = useState(null);
    const [catalogQueue, setCatalogQueue] = useState([]);
    const [catalogQueueTotal, setCatalogQueueTotal] = useState(0);

    // CSV import queue — each item only has an identifier (+ optional
    // title), never full data, so it goes through the normal search flow
    // one item at a time, same shape as the Buy List queue but generalized
    // to whichever identifier type the seller mapped (UPC/GTIN/SKU/Item ID/title).
    const [csvCurrentItem, setCsvCurrentItem] = useState(null); // { idType, idValue, title } | null
    const [csvQueue, setCsvQueue] = useState([]);
    const [csvQueueTotal, setCsvQueueTotal] = useState(0);
    const [csvItemKey, setCsvItemKey] = useState(0); // forces WalmartCatalogSearchStep to remount per item

    const STATUS_SORT_ORDER = { processed: 0, submitted: 0, processing: 1, failed: 2, queued: 3 };

    const sortByStatus = (items) => {
        return [...items].sort((a, b) => {
            const orderA = STATUS_SORT_ORDER[a.status] ?? 99;
            const orderB = STATUS_SORT_ORDER[b.status] ?? 99;
            return orderA - orderB;
        });
    };

    const refreshItems = async (batchId) => {
        const { items } = await listBatchItems(batchId);
        setProducts(sortByStatus(items || []));
    };

    const handleResumeBatch = async (existingBatch) => {
        setBatch(existingBatch);
        setBatchType({ id: existingBatch.batch_type, title: existingBatch.batch_type === "walmart_wfs" ? "Walmart WFS" : "Walmart SF" });
        setConsecutiveStillProcessing(0);
        setShowPatiencePopup(false);
        await refreshItems(existingBatch.id);
        setFlowStep("search");
        // if anything was left mid-flight from a previous session, check it
        // right away - this is how "close the tab, come back later" resumes.
        checkBatchProcessing(existingBatch.id)
            .then(() => refreshItems(existingBatch.id))
            .catch((err) => console.error("[checkBatchProcessing failed]", err));
    };

    useEffect(() => {
        const handler = (e) => {
            if (document.activeElement && document.activeElement.type === "number") {
                document.activeElement.blur();
            }
        };
        document.addEventListener("wheel", handler, { passive: true });
        return () => document.removeEventListener("wheel", handler);
    }, []);

    // While this batch is open, auto-check every 20s if anything's still
    // processing - purely a convenience for people staying on the page;
    // closing the tab loses nothing, since resuming re-checks on load anyway.
    useEffect(() => {
        if (!batch) return;
        const interval = setInterval(() => {
            const hasProcessing = products.some((p) => p.status === "processing");
            if (!hasProcessing) return;
            setCheckingStatus(true);
            checkBatchProcessing(batch.id)
                .then((result) => {
                    const stillWaiting = (result.still_processing || 0) > 0;
                    if (stillWaiting) {
                        setConsecutiveStillProcessing((c) => {
                            const next = c + 1;
                            if (next === 4) setShowPatiencePopup(true);
                            return next;
                        });
                    } else {
                        setConsecutiveStillProcessing(0);
                    }
                    const newFailures = (result.feeds_checked || []).flatMap((f) => f.partial_failures || []);
                    if (newFailures.length > 0) {
                        setShipError(`${newFailures.length} item${newFailures.length === 1 ? "" : "s"} failed processing — see the red status badge${newFailures.length === 1 ? "" : "s"} below for details.`);
                    }
                    return refreshItems(batch.id);
                })
                .catch((err) => console.error("[auto-check failed]", err))
                .finally(() => setCheckingStatus(false));
        }, 20000);
        return () => clearInterval(interval);
    }, [batch, products]);

    const handleSelectBatch = (type) => {
        setBatchType(type);
        setProducts([]);
        setFlowStep("name");
    };

    // "No batch" quick-start: reuses one hidden __quick__-prefixed batch per
    // type, deleting any old one first so it never accumulates leftover
    // items from a previous quick session — skips naming entirely and lands
    // straight on Find a Product.
    const [convertingBatchId, setConvertingBatchId] = useState(null); // sfBatch.id while conversion is in progress, for a per-row loading state

    // Pulls every item's best available identifier (UPC/GTIN from the
    // stored payload's productIdentifiers, falling back to SKU if neither
    // is present) out of an existing SF batch, creates a BRAND NEW real
    // WFS batch (never touching/closing the original SF batch), and queues
    // those identifiers through the normal search-and-add flow one at a
    // time — same mechanism as CSV import (csvQueue/csvCurrentItem), just
    // sourced from batch_items instead of an uploaded file.
    const handleConvertToWfs = async (sfBatch) => {
        setConvertingBatchId(sfBatch.id);
        try {
            const { items: sfItems } = await listBatchItems(sfBatch.id);
            if (!sfItems || sfItems.length === 0) {
                alert("This batch has no items to convert.");
                return;
            }
            const queueItems = sfItems.map((it) => {
                const ids = it.payload?.Orderable?.productIdentifiers || {};
                const idType = ids.productIdType ? ids.productIdType.toLowerCase() : "sku";
                const idValue = ids.productId || it.sku;
                return { idType, idValue, title: it.title || "" };
            }).filter((it) => it.idValue);

            const { batch: created } = await createBatch(`${sfBatch.name} (WFS)`, "walmart_wfs");
            setBatchType({ id: "walmart_wfs", title: "Walmart WFS" });
            setBatch(created);
            setProducts([]);

            const [first, ...rest] = queueItems;
            setPendingItem(null);
            setRetryItem(null);
            setCsvQueueTotal(queueItems.length);
            setCsvQueue(rest);
            setCsvCurrentItem(first);
            setCsvItemKey((k) => k + 1);
            setFlowStep("search");
        } catch (e) {
            alert(e.message || "Failed to start WFS conversion");
        } finally {
            setConvertingBatchId(null);
        }
    };

    const handleQuickStart = async (batchTypeId) => {
        const typeMeta = { id: batchTypeId, title: batchTypeId === "walmart_wfs" ? "Walmart WFS" : "Walmart SF" };
        const quickName = `${QUICK_BATCH_PREFIX}${batchTypeId}`;
        // Reuse the existing quick batch instead of deleting it — deleting
        // was the actual bug: two logins/tabs quick-starting around the same
        // time could race, with session A's delete yanking the batch out
        // from under session B mid-add, leaving B pointing at a batch_id
        // that no longer exists. Reusing avoids that entirely.
        // Still show an empty list on load, same as before — this is meant
        // to look/feel like a fresh "no batch" every time, so we don't load
        // whatever's already sitting in there from a previous session.
        // Nothing is lost: items are still on the batch server-side and
        // will appear normally once added-to in THIS session (via the
        // regular refreshItems() call after each add).
        const existing = await listBatches("open").catch(() => ({ batches: [] }));
        const reusable = (existing.batches || []).find((b) => b.name === quickName && b.batch_type === batchTypeId);
        const created = reusable || (await createBatch(quickName, batchTypeId)).batch;
        setBatchType(typeMeta);
        setBatch(created);
        setProducts([]);
        setFlowStep("search");
    };

    const handleBatchCreated = (createdBatch, destination = "search") => {
        setBatch(createdBatch);
        setFlowStep(destination);
    };

    const handleSelectExisting = (item) => { setPendingItem(item); setFlowStep("details"); };
    const handleCreateNew = () => { setPendingItem(null); setFlowStep("details"); };
    const handleRetryItem = (item) => { setRetryItem(item); setFlowStep("details"); };

    const handleBuylistImportSelected = (items) => {
        if (!items || items.length === 0) return;
        const [first, ...rest] = items;
        setPendingItem(null);
        setRetryItem(null);
        setBuylistQueueTotal(items.length);
        setBuylistQueue(rest);
        setBuylistCurrentItem(first);
        setBuylistItemKey((k) => k + 1);
        setFlowStep("search"); // land on Find a Product, not Create New Listing
    };

    const advanceBuylistQueue = () => {
        if (buylistQueue.length > 0) {
            const [next, ...rest] = buylistQueue;
            setBuylistCurrentItem(next);
            setBuylistQueue(rest);
            setBuylistItemKey((k) => k + 1);
        } else {
            setBuylistCurrentItem(null);
            setBuylistQueueTotal(0);
        }
        setFlowStep("search");
    };

    const handleSkipBuylistItem = () => advanceBuylistQueue();

    const handleCsvImportSelected = (items) => {
        if (!items || items.length === 0) return;
        const [first, ...rest] = items;
        setPendingItem(null);
        setRetryItem(null);
        setCsvQueueTotal(items.length);
        setCsvQueue(rest);
        setCsvCurrentItem(first);
        setCsvItemKey((k) => k + 1);
        setFlowStep("search");
    };

    const advanceCsvQueue = () => {
        if (csvQueue.length > 0) {
            const [next, ...rest] = csvQueue;
            setCsvCurrentItem(next);
            setCsvQueue(rest);
            setCsvItemKey((k) => k + 1);
        } else {
            setCsvCurrentItem(null);
            setCsvQueueTotal(0);
        }
        setFlowStep("search");
    };

    const handleSkipCsvItem = () => advanceCsvQueue();

    const handleCatalogImportSelected = (items) => {
        if (!items || items.length === 0) return;
        const [first, ...rest] = items;
        setPendingItem(mapCatalogItemToProduct(first));
        setRetryItem(null);
        setCatalogQueueTotal(items.length);
        setCatalogQueue(rest);
        setCatalogCurrentItem(first);
        setFlowStep("details");
    };

    const advanceCatalogQueue = () => {
        if (catalogQueue.length > 0) {
            const [next, ...rest] = catalogQueue;
            setPendingItem(mapCatalogItemToProduct(next));
            setCatalogCurrentItem(next);
            setCatalogQueue(rest);
            setFlowStep("details");
        } else {
            setCatalogCurrentItem(null);
            setCatalogQueueTotal(0);
            setFlowStep("search");
        }
    };

    const handleDetailsBack = () => {
        setPendingItem(null);
        setRetryItem(null);
        if (catalogCurrentItem) {
            // Mid-catalog-queue back navigation has no "search" step to return
            // to (it was skipped entirely) — send them back to the picker instead.
            setCatalogCurrentItem(null);
            setCatalogQueue([]);
            setCatalogQueueTotal(0);
            setFlowStep("catalog_pick");
            return;
        }
        setFlowStep("search"); // any in-progress buy-list/CSV session stays intact
    };

    const handleAddToBatch = async () => {
        if (retryItem) { try { await removeBatchItem(batch.id, retryItem.id); } catch { } }
        await refreshItems(batch.id);
        setPendingItem(null);
        setRetryItem(null);
        if (buylistCurrentItem) {
            advanceBuylistQueue();
            return;
        }
        if (catalogCurrentItem) {
            advanceCatalogQueue();
            return;
        }
        if (csvCurrentItem) {
            advanceCsvQueue();
            return;
        }
        setFlowStep("search");
    };

    const handleRemoveItem = async (id) => {
        await removeBatchItem(batch.id, id);
        await refreshItems(batch.id);
    };

    // Submitting no longer blocks the UI waiting for Walmart to finish - it
    // can take minutes to hours. We just kick off the feed, flip items to
    // 'processing', and let the user check back (page load or manual
    // refresh) rather than holding the tab open.
    const handleShipAll = async () => {
        setShipping(true);
        setShipError("");
        try {
            const feedType = products.find((p) => p.status === "queued")?.feed_type || "MP_ITEM";
            await flushBatch(batch.id, feedType);
            await refreshItems(batch.id); // items now show 'processing'
        } catch (err) {
            setShipError(err.message || "Failed to send to Walmart");
        } finally {
            setShipping(false);
        }
    };

    const [checkingStatus, setCheckingStatus] = useState(false);
    const [consecutiveStillProcessing, setConsecutiveStillProcessing] = useState(0);
    const [showPatiencePopup, setShowPatiencePopup] = useState(false);

    const handleCheckStatus = async () => {
        setCheckingStatus(true);
        setShipError("");
        try {
            const result = await checkBatchProcessing(batch.id);
            const stillWaiting = (result.still_processing || 0) > 0;
            if (stillWaiting) {
                setConsecutiveStillProcessing((c) => {
                    const next = c + 1;
                    if (next === 4) setShowPatiencePopup(true);
                    return next;
                });
            } else {
                setConsecutiveStillProcessing(0);
            }

            // Surface partial failures immediately — a feed can finish with
            // SOME items processed successfully and others rejected; the red
            // "failed" badge on each item already shows this after refresh,
            // but this catches the user's attention right when it happens
            // instead of relying on them to notice a badge change.
            const newFailures = (result.feeds_checked || []).flatMap((f) => f.partial_failures || []);
            if (newFailures.length > 0) {
                setShipError(`${newFailures.length} item${newFailures.length === 1 ? "" : "s"} failed processing — see the red status badge${newFailures.length === 1 ? "" : "s"} below for details.`);
            }

            await refreshItems(batch.id);
        } catch (err) {
            setShipError(err.message || "Failed to check status");
        } finally {
            setCheckingStatus(false);
        }
    };

    if (flowStep === "resume") return <ResumeBatchStep onSelect={handleResumeBatch} onStartNew={() => setFlowStep("type")} onQuickStart={handleQuickStart} onConvertToWfs={handleConvertToWfs} convertingBatchId={convertingBatchId} />;
    if (flowStep === "type") return <BatchTypeStep onSelect={handleSelectBatch} />;
    if (flowStep === "name") return <BatchNameStep batchType={batchType} onSubmit={handleBatchCreated} onBack={() => setFlowStep("type")} />;

    return (
        <>
            {buylistCurrentItem && (
                <div className="max-w-6xl mx-auto px-6 pt-6">
                    <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3 flex items-center justify-between flex-wrap gap-2">
                        <p className="text-sm text-purple-700 font-semibold">
                            📋 Importing from Buy List — item {buylistQueueTotal - buylistQueue.length} of {buylistQueueTotal}
                            {buylistCurrentItem.upc ? ` · UPC ${buylistCurrentItem.upc}` : buylistCurrentItem.title ? ` · ${buylistCurrentItem.title}` : ""}
                        </p>
                        <div className="flex items-center gap-3">
                            <button onClick={() => setShowBuylistDataModal(true)} className="text-xs font-bold text-purple-600 hover:text-purple-800 underline">
                                Click here to see all saved data for this item
                            </button>
                            <button onClick={handleSkipBuylistItem} className="text-xs font-bold text-purple-600 hover:text-purple-800 underline">
                                Skip this item →
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {catalogCurrentItem && (
                <div className="max-w-6xl mx-auto px-6 pt-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center justify-between flex-wrap gap-2">
                        <p className="text-sm text-blue-700 font-semibold">
                            🗂️ Adding from Catalog — item {catalogQueueTotal - catalogQueue.length} of {catalogQueueTotal}
                            {catalogCurrentItem.sku ? ` · SKU ${catalogCurrentItem.sku}` : catalogCurrentItem.title ? ` · ${catalogCurrentItem.title}` : ""}
                        </p>
                        <button onClick={advanceCatalogQueue} className="text-xs font-bold text-blue-600 hover:text-blue-800 underline">
                            Skip this item →
                        </button>
                    </div>
                </div>
            )}
            {csvCurrentItem && (
                <div className="max-w-6xl mx-auto px-6 pt-6">
                    <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center justify-between flex-wrap gap-2">
                        <p className="text-sm text-green-700 font-semibold">
                            📄 Importing from CSV — item {csvQueueTotal - csvQueue.length} of {csvQueueTotal}
                            {" · "}{csvCurrentItem.idType.toUpperCase()} {csvCurrentItem.idValue}
                            {csvCurrentItem.title ? ` · ${csvCurrentItem.title}` : ""}
                        </p>
                        <button onClick={handleSkipCsvItem} className="text-xs font-bold text-green-600 hover:text-green-800 underline">
                            Skip this item →
                        </button>
                    </div>
                </div>
            )}
            {showBuylistDataModal && (
                <BuylistItemDataModal item={buylistCurrentItem} onClose={() => setShowBuylistDataModal(false)} />
            )}
            <div className="max-w-6xl mx-auto py-8 px-6 flex flex-col lg:flex-row gap-6 relative">
                {showPatiencePopup && (
                    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
                            <div className="text-3xl mb-3">⏳</div>
                            <h3 className="font-bold text-gray-800 mb-2">Walmart is taking a bit longer</h3>
                            <p className="text-sm text-gray-500 mb-5">
                                Sometimes Walmart's processing can take a while - even a few hours for larger
                                batches. Feel free to close this page and come back later, we'll pick up right where
                                we left off and finish automatically once Walmart confirms.
                            </p>
                            <button
                                onClick={() => { setShowPatiencePopup(false); }}
                                className="w-full py-2.5 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition"
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                )}
                {flowStep === "search" && (
                    <WalmartCatalogSearchStep
                        key={csvCurrentItem ? `csv-${csvItemKey}` : buylistItemKey}
                        onSelectExisting={handleSelectExisting}
                        onCreateNew={handleCreateNew}
                        onBack={() => setFlowStep("resume")}
                        onImportFromBuylist={() => setFlowStep("buylist_pick")}
                        onImportFromCatalog={() => setFlowStep("catalog_pick")}
                        onImportFromCsv={() => setFlowStep("csv_pick")}
                        initialSearchType={csvCurrentItem ? csvCurrentItem.idType : (buylistCurrentItem?.upc ? "upc" : (quickAddItemId ? "item_id" : undefined))}
                        initialSearchValue={csvCurrentItem ? csvCurrentItem.idValue : (buylistCurrentItem?.upc || quickAddItemId || "")}
                        autoSearch={!!csvCurrentItem || !!buylistCurrentItem?.upc || !!quickAddItemId}
                    />
                )}
                {flowStep === "buylist_pick" && (
                    <BuylistImportStep onImportSelected={handleBuylistImportSelected} onBack={() => setFlowStep("search")} />
                )}
                {flowStep === "catalog_pick" && (
                    <CatalogImportStep onImportSelected={handleCatalogImportSelected} onBack={() => setFlowStep("search")} />
                )}
                {flowStep === "csv_pick" && (
                    <CsvImportStep onImportSelected={handleCsvImportSelected} onBack={() => setFlowStep("search")} />
                )}
                {flowStep === "details" && (
                    <WalmartItemDetails
                        batchType={batchType} batchId={batch.id} initialProduct={pendingItem} retryItem={retryItem}
                        prefillUpc={buylistCurrentItem?.upc}
                        prefillName={buylistCurrentItem?.title}
                        prefillDescription={buylistCurrentItem?.description}
                        prefillImageUrl={buylistCurrentItem?.image_url}
                        buylistFields={buylistCurrentItem?.allFields || null}
                        onAddToBatch={handleAddToBatch}
                        onBack={handleDetailsBack}
                    />
                )}
                <BatchSummarySidebar
                    batchName={batch.name} batchType={batchType} products={products}
                    batchId={batch.id}
                    onRemoveItem={handleRemoveItem} onRetryItem={handleRetryItem} onShipAll={handleShipAll}
                    shipping={shipping} shipError={shipError}
                    onPrintLabels={() => setShowLabelsModal(true)}
                    onCheckStatus={handleCheckStatus} checkingStatus={checkingStatus}
                    onRefresh={() => refreshItems(batch.id)}
                />
                {showLabelsModal && (
                    <PrintLabelsModal products={products} onClose={() => setShowLabelsModal(false)} />
                )}
            </div>
        </>
    );
};

export default BatchesPage;